/**
 * ============================================================================
 * CAMADA ÚNICA DE ACESSO A API.  Nenhuma tela fala com a rede fora daqui.
 * ============================================================================
 *
 * Os tipos NÃO são mais escritos à mão: saem do próprio `AppRouter`
 * (`apps/api/src/trpc/router.ts`, exposto pelo campo `types` do pacote). Se o
 * servidor mudar a forma de um retorno, o erro aparece aqui, no typecheck —
 * não em produção, três telas adiante.
 *
 * ----------------------------------------------------------------------------
 * CONTRATO REAL (conferido em 15/09 contra apps/api):
 *
 *   workspace.listMine                                  -> WorkspaceSummary[]
 *   workspace.create   { name, timezone?, locale? }     -> WorkspaceSummary
 *   workspace.get      { workspaceId }                  -> workspace + role
 *   workspace.update   { workspaceId, name?, timezone?, locale? }
 *                                                       -> workspace SEM role
 *   member.list        { workspaceId }  -> { members, invitations, seats }
 *   member.invite      { workspaceId, email, role }     -> convite pendente
 *   member.updateRole  { workspaceId, userId, role }    -> { updated: true }
 *   member.remove      { workspaceId, userId }          -> { removed: true }
 *   member.revokeInvite{ workspaceId, invitationId }    -> { revoked: true }
 *   member.transferOwnership { workspaceId, userId }    -> { transferred: true }
 *
 * DIFERENÇAS em relação ao que este app assumia antes (todas já corrigidas):
 *   - a listagem é `listMine`, não `list`;
 *   - `transferOwnership` vive em `member`, não em `workspace`;
 *   - as mutações de membro endereçam por `userId`, não por um `memberId`;
 *   - `member.list` devolve um OBJETO (membros + convites + assentos), não um
 *     array — convite pendente é uma lista própria, com revogação própria.
 *
 * Métrica não entra nesta lista: KPI vem pronto do servidor (invariante 3) e a
 * Fase 6 é quem define esses procedures.
 * ----------------------------------------------------------------------------
 */
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@mgdlead/api';
import { WORKSPACE_ROLES } from '@mgdlead/shared';
import type { WorkspaceRole } from '@mgdlead/shared';

import { httpStatusFromError, isAuthError } from './errors';

/* ========================================================================== */
/*  Endereço                                                                   */
/* ========================================================================== */

/**
 * Origem da api. Vazia em desenvolvimento: o app fala com a PRÓPRIA origem e o
 * proxy do Vite leva até `http://localhost:3001`. Mesma origem = cookie de
 * sessão sem CORS e sem SameSite=None, que o Safari descarta.
 * Só se preenche quando api e app ficam em domínios diferentes.
 */
export const API_ORIGIN: string = import.meta.env.VITE_API_URL ?? '';

/**
 * A api monta o tRPC na RAIZ (`fastifyTRPCPlugin` com `prefix: '/trpc'`), não
 * em `/api/trpc`. Assumir um `/api` comum ao tRPC e ao Better Auth rendia 404
 * em toda chamada — conferido contra a api rodando, não deduzido.
 *
 * O Better Auth fica em `/api/auth` (`AUTH_BASE_PATH` da api), que por acaso é
 * o default do próprio Better Auth — por isso `lib/auth.ts` não passa `baseURL`
 * quando api e app estão na mesma origem.
 */
export const TRPC_URL = `${API_ORIGIN}/trpc`;

/* ========================================================================== */
/*  Cliente                                                                    */
/* ========================================================================== */

/**
 * `useTRPCClient` entra aqui por causa de `billing` (Fase 10): esse router foi
 * escrito como CONTRATO antes de existir no código, então o proxy tipado ainda
 * não tem `billing.*` para oferecer. O cliente cru é o único caminho até ele —
 * e continua sendo ESTE arquivo falando com a rede, que é a regra da casa.
 */
export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

export function createApiClient() {
  return createTRPCClient<AppRouter>({
    links: [
      httpBatchLink({
        url: TRPC_URL,
        /**
         * Lote grande vira vários lotes menores em vez de uma URL gigante. A api aceita
         * caminhos longos (maxParamLength), mas proxy e CDN no caminho têm limites próprios —
         * o 414 já derrubou o Dashboard uma vez (06/10).
         */
        maxURLLength: 2_000,
        // o cookie de sessão precisa viajar junto
        fetch: (input, init) => fetch(input, { ...init, credentials: 'include' }),
      }),
    ],
  });
}

/**
 * Defaults do React Query.
 *
 * `retry` que repete 401/403 é pior que inútil: multiplica requisição morta e
 * atrasa em 3x o redirecionamento para o login. Qualquer 4xx é resposta final.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount: number, error: Error) => {
          if (isAuthError(error)) return false;
          const status = httpStatusFromError(error);
          if (status !== null && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt: number) => Math.min(1_000 * 2 ** attempt, 8_000),
      },
      mutations: {
        // mutation não se repete sozinha: convidar duas vezes manda dois e-mails
        retry: false,
      },
    },
  });
}

/* ========================================================================== */
/*  DTOs — derivados do router, não escritos à mão                             */
/* ========================================================================== */

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * `inferRouterOutputs` moraria em `@trpc/server`, que não é (nem precisa ser)
 * dependência do front. O cliente decorado já carrega a mesma informação: o
 * retorno de `.query()` / `.mutate()` É a saída do procedure.
 */
export type QueryOutput<TProcedure extends { query: (...args: never[]) => unknown }> = Awaited<
  ReturnType<TProcedure['query']>
>;
type MutationOutput<TProcedure extends { mutate: (...args: never[]) => unknown }> = Awaited<
  ReturnType<TProcedure['mutate']>
>;
type MutationInput<TProcedure extends { mutate: (input: never, ...rest: never[]) => unknown }> =
  Parameters<TProcedure['mutate']>[0];

export type WorkspaceSummary = QueryOutput<ApiClient['workspace']['listMine']>[number];

/**
 * `workspace.listMine` declara `role: string` do lado da api (o tipo escrito a
 * mão em `workspace.service.ts` alarga o enum do banco). Como o papel decide o
 * menu inteiro, ele não entra no app sem conferência.
 *
 * Valor desconhecido cai em MEMBER — o papel de MENOR privilégio. Errar para
 * baixo esconde um botão; errar para cima mostra uma ação que o servidor vai
 * recusar, e aí o usuário acha que o produto quebrou.
 */
export function toWorkspaceRole(value: string): WorkspaceRole {
  return (WORKSPACE_ROLES as readonly string[]).includes(value)
    ? (value as WorkspaceRole)
    : 'MEMBER';
}

/** Papéis que se pode atribuir: OWNER só muda por transferência de propriedade. */
export const ASSIGNABLE_ROLES = ['ADMIN', 'ANALYST', 'MEMBER'] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export function isAssignableRole(value: string): value is AssignableRole {
  return (ASSIGNABLE_ROLES as readonly string[]).includes(value);
}

export type MemberList = QueryOutput<ApiClient['member']['list']>;
export type Member = MemberList['members'][number];
export type Invitation = MemberList['invitations'][number];
/** Avaliação do limite de assentos do plano — vem pronta do EntitlementService. */
export type SeatUsage = MemberList['seats'];

export type CreateWorkspaceInput = MutationInput<ApiClient['workspace']['create']>;
export type UpdateWorkspaceInput = MutationInput<ApiClient['workspace']['update']>;
export type InviteMemberInput = MutationInput<ApiClient['member']['invite']>;
export type UpdateMemberRoleInput = MutationInput<ApiClient['member']['updateRole']>;
export type RemoveMemberInput = MutationInput<ApiClient['member']['remove']>;
export type RevokeInviteInput = MutationInput<ApiClient['member']['revokeInvite']>;
export type TransferOwnershipInput = MutationInput<ApiClient['member']['transferOwnership']>;
export type InviteResult = MutationOutput<ApiClient['member']['invite']>;

/* ========================================================================== */
/*  Hooks                                                                      */
/* ========================================================================== */

export function useWorkspaces() {
  const trpc = useTRPC();
  return useQuery(trpc.workspace.listMine.queryOptions());
}

/** Invalida a lista de workspaces (nome, fuso e papel saem de lá). */
function useInvalidateWorkspaces() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.workspace.listMine.queryKey() });
  };
}

export function useCreateWorkspace() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateWorkspaces();
  return useMutation(
    trpc.workspace.create.mutationOptions({
      onSuccess: (workspace) => {
        /**
         * Escreve o workspace novo no cache ANTES de qualquer navegação.
         *
         * Sem isto, a sequência real era: create 200 → navega para /w/:slug → o guard
         * lê a lista ainda vazia do cache → devolve para /onboarding → e só então o
         * refetch traz o workspace, tarde demais. O usuário criava um workspace
         * duplicado a cada nova tentativa, no primeiro minuto de uso.
         *
         * `invalidate()` continua logo abaixo para reconciliar com o servidor, mas a
         * navegação não depende mais dele.
         */
        queryClient.setQueryData(trpc.workspace.listMine.queryKey(), (atual) =>
          atual ? [...atual, workspace] : [workspace],
        );
        invalidate();
      },
    }),
  );
}

export function useUpdateWorkspace() {
  const trpc = useTRPC();
  const invalidate = useInvalidateWorkspaces();
  return useMutation(trpc.workspace.update.mutationOptions({ onSuccess: invalidate }));
}

/* -------------------------------------------------------------------------- */
/*  Avisos de número fora do ar (06/10)                                        */
/* -------------------------------------------------------------------------- */

export type AlertSettings = QueryOutput<ApiClient['alerts']['get']>;

export function useAlertSettings(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery(trpc.alerts.get.queryOptions({ workspaceId }));
}

export function useUpdateAlertSettings() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.alerts.update.mutationOptions({
      onSuccess: (data, input) => {
        queryClient.setQueryData(
          trpc.alerts.get.queryKey({ workspaceId: input.workspaceId }),
          data,
        );
      },
    }),
  );
}

export function useSendTestAlert() {
  const trpc = useTRPC();
  return useMutation(trpc.alerts.sendTest.mutationOptions());
}

export function useMembers(workspaceId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.member.list.queryOptions({ workspaceId: workspaceId ?? '' }),
    enabled: workspaceId !== null,
  });
}

/**
 * Depois de qualquer escrita em membro, tanto a lista de membros quanto a de
 * workspaces saem do lugar: transferir propriedade muda o PAPEL de quem está
 * olhando, e o papel é o que decide o menu inteiro.
 */
function useInvalidateMembers() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.member.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.workspace.listMine.queryKey() });
  };
}

export function useInviteMember() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMembers();
  return useMutation(trpc.member.invite.mutationOptions({ onSuccess: invalidate }));
}

export function useUpdateMemberRole() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMembers();
  return useMutation(trpc.member.updateRole.mutationOptions({ onSuccess: invalidate }));
}

export function useRemoveMember() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMembers();
  return useMutation(trpc.member.remove.mutationOptions({ onSuccess: invalidate }));
}

export function useRevokeInvite() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMembers();
  return useMutation(trpc.member.revokeInvite.mutationOptions({ onSuccess: invalidate }));
}

export function useTransferOwnership() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMembers();
  return useMutation(trpc.member.transferOwnership.mutationOptions({ onSuccess: invalidate }));
}

/* ========================================================================== */
/*  Monitoradores (Fase 2)                                                     */
/* ========================================================================== */

/**
 * CONTRATO REAL (conferido em `apps/api/src/modules/monitor/monitor.router.ts`):
 *
 *   monitor.list       { workspaceId, includeArchived? }
 *                        -> { monitors, archivedCount, limit }
 *   monitor.get        { workspaceId, monitorId, includeArchived? } -> MonitorRow
 *   monitor.create     { workspaceId, name, provider? }             -> MonitorRow
 *   monitor.rename     { workspaceId, monitorId, name }             -> MonitorRow
 *   monitor.archive    { workspaceId, monitorId }  -> { archived: true }
 *   monitor.remove     { workspaceId, monitorId }  -> { removed: true }   (exige arquivado antes)
 *   monitor.connect    { workspaceId, monitorId }  -> { monitorId, status, health, needsQr }
 *   monitor.qr         { workspaceId, monitorId }  -> QR | ALREADY_CONNECTED | PENDING
 *                                                     | NOT_STARTED | ERROR
 *   monitor.disconnect { workspaceId, monitorId }  -> MonitorRow
 *   monitor.reconnect  { workspaceId, monitorId }  -> ConnectResult
 *   monitor.syncStatus { workspaceId, monitorId }  -> MonitorRow + providerStatus + silentForMs
 *   monitor.health     { workspaceId, monitorId }  -> status, health, providerStatus,
 *                        providerHealthy, providerVersion, silentForMs, silenceThresholdMs,
 *                        lastEventAt, lastError
 *
 * Duas coisas que a tela NÃO pode esquecer:
 *  - `limit` chega AVALIADO (`allowed`, `remaining`, `unlimited`). Refazer a conta
 *    aqui é a invariante 4 do CLAUDE.md sendo quebrada.
 *  - `monitor.health` é query mas ESCREVE: ela reconcilia com o provider antes de
 *    responder. Por isso nunca entra em polling e invalida a lista ao voltar.
 */

export type MonitorList = QueryOutput<ApiClient['monitor']['list']>;
export type Monitor = MonitorList['monitors'][number];
/** Avaliação do limite de monitoradores do plano — vem pronta do EntitlementService. */
export type MonitorLimit = MonitorList['limit'];
export type MonitorQrState = QueryOutput<ApiClient['monitor']['qr']>;
/** Nome do estado do QR: `"QR" | "ALREADY_CONNECTED" | "PENDING" | "NOT_STARTED" | "ERROR"`. */
export type MonitorQrStateName = MonitorQrState['state'];
export type MonitorHealthReport = QueryOutput<ApiClient['monitor']['health']>;
export type MonitorConnectResult = MutationOutput<ApiClient['monitor']['connect']>;

export type CreateMonitorInput = MutationInput<ApiClient['monitor']['create']>;
export type RenameMonitorInput = MutationInput<ApiClient['monitor']['rename']>;
export type MonitorRefInput = MutationInput<ApiClient['monitor']['connect']>;

export function useMonitors(workspaceId: string, includeArchived = false) {
  const trpc = useTRPC();
  // A cada minuto: é a lista que acende a faixa de "número caiu" e o selo do WhatsApp.
  return useQuery({
    ...trpc.monitor.list.queryOptions({ workspaceId, includeArchived }),
    refetchInterval: 60_000,
  });
}

/**
 * Recarrega TUDO que o painel mostra: números de analytics, monitores, Meta e incidentes.
 * É o que os botões ↻ chamam quando a leitura pedida termina — sem isto o botão girava, a
 * leitura acontecia no servidor e a tela continuava mostrando o número velho (dono, 06/10).
 */
export function useRecarregarPainel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    for (const key of [
      trpc.analytics.pathKey(),
      trpc.monitor.list.pathKey(),
      trpc.ads.pathKey(),
      trpc.incident.pathKey(),
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

export type CampaignBudget = QueryOutput<ApiClient['ads']['budgets']>[number];

/** Orçamento diário das campanhas da Meta de um bloco (janela "Ajustar orçamento"). */
export function useCampaignBudgets(workspaceId: string, externalIds: string[], enabled: boolean) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.ads.budgets.queryOptions({ workspaceId, externalIds }),
    enabled: enabled && externalIds.length > 0,
  });
}

/** Altera o orçamento NA META. Recarrega o painel e a janela quando dá certo. */
export function useUpdateBudget() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.ads.updateBudget.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.ads.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.analytics.pathKey() });
      },
    }),
  );
}

/** "Atualizar WhatsApp" do Dashboard: relê os grupos de todos os números (trava de 10 min). */
export function useResyncWhatsAppNow() {
  const trpc = useTRPC();
  return useMutation(trpc.monitor.resyncNow.mutationOptions());
}

/**
 * Invalida tudo que uma escrita em monitorador desloca: a lista (estado, saúde,
 * contagem de arquivados e o limite do plano) e a sonda de saúde aberta.
 */
function useInvalidateMonitors() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.monitor.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.monitor.health.queryKey() });
  };
}

export function useCreateMonitor() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitors();
  /**
   * Sem `setQueryData` aqui, de propósito — e o motivo é o mesmo de
   * `useCreateWorkspace`, só que com a conclusão oposta.
   *
   * Lá o passo seguinte (navegar para `/w/:slug`) LIA o cache, e por isso
   * depender do refetch mandava o usuário de volta para o onboarding. Aqui o
   * passo seguinte — conectar e abrir o QR — usa o `monitorId` que a própria
   * mutação devolve; nada no caminho crítico lê a lista. Escrever no cache só
   * deixaria `limit.current` desatualizado por um instante, que é justamente o
   * número que o cliente não tem o direito de inventar.
   */
  return useMutation(trpc.monitor.create.mutationOptions({ onSuccess: invalidate }));
}

export function useRenameMonitor() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitors();
  return useMutation(trpc.monitor.rename.mutationOptions({ onSuccess: invalidate }));
}

export function useArchiveMonitor() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitors();
  return useMutation(trpc.monitor.archive.mutationOptions({ onSuccess: invalidate }));
}

export function useRemoveMonitor() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitors();
  return useMutation(trpc.monitor.remove.mutationOptions({ onSuccess: invalidate }));
}

export function useConnectMonitor() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitors();
  return useMutation(trpc.monitor.connect.mutationOptions({ onSuccess: invalidate }));
}

export function useReconnectMonitor() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitors();
  return useMutation(trpc.monitor.reconnect.mutationOptions({ onSuccess: invalidate }));
}

export function useDisconnectMonitor() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitors();
  return useMutation(trpc.monitor.disconnect.mutationOptions({ onSuccess: invalidate }));
}

export function useSyncMonitorStatus() {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitors();
  return useMutation(trpc.monitor.syncStatus.mutationOptions({ onSuccess: invalidate }));
}

export interface MonitorQrOptions {
  /** `false` derruba a consulta inteira — é o freio de mão do polling. */
  enabled: boolean;
  /** Intervalo do polling em ms. `false` = consulta uma vez e para. */
  refetchInterval: number | false;
}

/**
 * QR do monitorador.
 *
 * O intervalo é OBRIGATÓRIO e vem de fora: o QR do WhatsApp expira em segundos,
 * então quem decide de quanto em quanto tempo repetir — e, principalmente, QUANDO
 * PARAR — é a tela que abriu o drawer, não este hook.
 *
 * `staleTime: 0` + `gcTime: 0` porque QR guardado é QR vencido: reabrir o drawer
 * precisa pedir um novo, nunca pintar o da vez passada.
 * `refetchIntervalInBackground: false` impede a aba esquecida de continuar pedindo.
 */
export function useMonitorQr(
  workspaceId: string,
  monitorId: string | null,
  options: MonitorQrOptions,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.monitor.qr.queryOptions({ workspaceId, monitorId: monitorId ?? '' }),
    enabled: options.enabled && monitorId !== null,
    refetchInterval: options.enabled ? options.refetchInterval : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/**
 * Sonda de saúde. Só roda com o detalhe aberto (`enabled`), e nunca em intervalo:
 * cada chamada conversa com o provider e reescreve a linha.
 */
export function useMonitorHealth(workspaceId: string, monitorId: string | null, enabled: boolean) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.monitor.health.queryOptions({ workspaceId, monitorId: monitorId ?? '' }),
    enabled: enabled && monitorId !== null,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * A faixa de atividade do monitorador — um quadradinho por dia (pedido do dono, 19/09).
 *
 * `staleTime` generoso de propósito: a faixa é histórico, muda no máximo de 5 em 5 minutos
 * (a cadência da varredura que a alimenta), e repuxá-la a cada foco de aba gastaria banco
 * para redesenhar os mesmos 30 quadradinhos.
 */
export function useMonitorActivity(
  workspaceId: string,
  monitorId: string | null,
  enabled: boolean,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.monitor.atividade.queryOptions({ workspaceId, monitorId: monitorId ?? '', dias: 30 }),
    enabled: enabled && monitorId !== null,
    staleTime: 5 * 60_000,
  });
}

/* ========================================================================== */
/*  Grupos (Fase 3)                                                           */
/* ========================================================================== */

/**
 * CONTRATO REAL (conferido em `apps/api/src/modules/group/group.router.ts`):
 *
 *   group.list             { workspaceId, includeArchived? }
 *                            -> { groups, archivedCount, limit }
 *   group.get              { workspaceId, groupId, includeArchived? }  -> GroupRow
 *   group.listAvailable    { workspaceId, monitorId }
 *                            -> { groups, seen, complete, importedCount, archivedCount, limit }
 *   group.importFromMonitor{ workspaceId, monitorId, providerGroupIds? }
 *                            -> { mode, imported, updated, skippedByLimit, notFound, seen, limit }
 *   group.update           { workspaceId, groupId, safetyThreshold?, position?, isActive? }
 *                            -> GroupRow
 *   group.archive          { workspaceId, groupId }  -> { ok: true }
 *   group.unarchive        { workspaceId, groupId }  -> GroupRow
 *
 * Cinco coisas que a tela NÃO pode esquecer:
 *  - `participantsCount` é `number | null`. `null` é "nunca sincronizou", e **não** zero
 *    (invariante 8). Quem testar com `if (!count)` quebra a regra em silêncio.
 *  - Lotação se mede contra `safetyThreshold`, nunca contra `maxParticipants`.
 *  - `limit` chega AVALIADO. Refazer a conta aqui quebra a invariante 4.
 *  - `listAvailable.complete === false` quer dizer "esta lista é um pedaço". A tela
 *    precisa DIZER isso; mostrar 100 de 250 calada é mentir com número redondo.
 *  - `importFromMonitor` SEM `providerGroupIds` é re-sincronização: atualiza o que já é
 *    monitorado e não traz grupo novo. Para importar, mande os ids escolhidos.
 */

export type GroupList = QueryOutput<ApiClient['group']['list']>;
export type Group = GroupList['groups'][number];
/** Avaliação do limite de grupos do plano — vem pronta do EntitlementService. */
export type GroupLimit = GroupList['limit'];
export type AvailableGroupList = QueryOutput<ApiClient['group']['listAvailable']>;
export type AvailableGroup = AvailableGroupList['groups'][number];
export type ImportGroupsResult = MutationOutput<ApiClient['group']['importFromMonitor']>;
export type UpdateGroupInput = MutationInput<ApiClient['group']['update']>;

export function useGroups(workspaceId: string, includeArchived = false) {
  const trpc = useTRPC();
  return useQuery(trpc.group.list.queryOptions({ workspaceId, includeArchived }));
}

/**
 * Os grupos que o número enxerga no WhatsApp — a lista de onde se escolhe.
 *
 * `monitorId` nulo desliga a consulta: cada chamada conversa com o provider e varre as
 * páginas dele, então ela só roda com o diálogo aberto e um número escolhido.
 *
 * `staleTime: 0` + `gcTime: 0` porque esta lista envelhece do lado de fora (a pessoa entra
 * e sai de grupo no celular): reabrir o diálogo tem que pedir de novo, não pintar o retrato
 * da vez passada.
 */
export function useAvailableGroups(workspaceId: string, monitorId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.group.listAvailable.queryOptions({ workspaceId, monitorId: monitorId ?? '' }),
    enabled: monitorId !== null && monitorId !== '',
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/**
 * Invalida o que uma escrita em grupo desloca: a própria lista (com o limite do plano),
 * a de monitoradores — `groupsCount` vive lá e é recontado a cada importação — e a lista
 * de disponíveis, onde cada linha carrega o "já monitorado" que acabou de mudar.
 */
function useInvalidateGroups() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.group.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.group.listAvailable.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.monitor.list.queryKey() });
  };
}

export function useImportGroups() {
  const trpc = useTRPC();
  const invalidate = useInvalidateGroups();
  return useMutation(trpc.group.importFromMonitor.mutationOptions({ onSuccess: invalidate }));
}

export function useUpdateGroup() {
  const trpc = useTRPC();
  const invalidate = useInvalidateGroups();
  return useMutation(trpc.group.update.mutationOptions({ onSuccess: invalidate }));
}

export function useArchiveGroup() {
  const trpc = useTRPC();
  const invalidate = useInvalidateGroups();
  return useMutation(trpc.group.archive.mutationOptions({ onSuccess: invalidate }));
}

export function useUnarchiveGroup() {
  const trpc = useTRPC();
  const invalidate = useInvalidateGroups();
  return useMutation(trpc.group.unarchive.mutationOptions({ onSuccess: invalidate }));
}

export function useRemoveGroup() {
  const trpc = useTRPC();
  const invalidate = useInvalidateGroups();
  return useMutation(trpc.group.remove.mutationOptions({ onSuccess: invalidate }));
}

/**
 * A varredura completa de participantes.
 *
 * Invalida a mesma lista das outras escritas porque mexe em duas coisas que a tela mostra:
 * `participantsCount` (recontado, não somado) e `participantsSyncedAt` — que é o que separa
 * "sei quantos são" de "sei quem são".
 */
export function useSyncGroupParticipants() {
  const trpc = useTRPC();
  const invalidate = useInvalidateGroups();
  return useMutation(trpc.group.syncParticipants.mutationOptions({ onSuccess: invalidate }));
}

export type RefreshInviteResult = MutationOutput<ApiClient['group']['refreshInvite']>;

/**
 * (Re)buscar o convite do grupo no WhatsApp.
 *
 * Invalida MAIS coisa que as outras escritas de grupo, e por um motivo: o convite é o que
 * decide se o grupo é elegível a receber clique (`invite_code is not null` no allocator).
 * Gravar um convite muda a prévia de alocação de TODO redirecionador cuja campanha tenha
 * este grupo — deixar `redirector.nextTargets` com o retrato velho faria a tela continuar
 * dizendo "nenhum grupo pode receber" logo depois de o problema ter sido resolvido.
 *
 * `group.get` entra porque a tela de detalhe do grupo mostra o estado do convite, e ela não
 * passa pela lista.
 */
export function useRefreshGroupInvite() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateGroups();
  return useMutation(
    trpc.group.refreshInvite.mutationOptions({
      onSuccess: () => {
        invalidate();
        void queryClient.invalidateQueries({ queryKey: trpc.group.get.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.redirector.nextTargets.queryKey() });
      },
    }),
  );
}

/* ========================================================================== */
/*  Convite, categorias e lotação — frente "grupos" (D1–D5, 07/10/2026)       */
/* ========================================================================== */

/**
 * CONTRATO REAL (conferido em `group.router.ts`, `category.router.ts`, `campaign.router.ts`):
 *
 *   group.pasteInvite     { workspaceId, groupId, link }
 *                           -> { status: 'RESOLVED'|'WRONG_GROUP'|'INVALID'|'MALFORMED'|'UNCONFIRMED' }
 *   group.linkChecks      { workspaceId, groupId } -> { checks, total, succeeded, successRate, lastError }
 *   group.setCategory     { workspaceId, groupId, categoryId|null } -> GroupRow
 *   groupCategory.list    { workspaceId } -> categorias com o recorte (grupos, gente, vagas)
 *   groupCategory.create  { workspaceId, name, color }
 *   groupCategory.update  { workspaceId, categoryId, name?, color? }
 *   groupCategory.remove  { workspaceId, categoryId }
 *   campaign.capacity     { workspaceId, campaignId? } -> lotação agregada por campanha
 *
 * `UNCONFIRMED` NÃO é erro: o WhatsApp não respondeu, nada mudou e o incidente segue aberto.
 * `successRate`/`occupancy` `null` = sem medição (≠ 0%).
 */
export type PasteInviteResult = MutationOutput<ApiClient['group']['pasteInvite']>;
export type LinkCheckHistory = QueryOutput<ApiClient['group']['linkChecks']>;
export type GroupCategory = QueryOutput<ApiClient['groupCategory']['list']>[number];
export type CampaignCapacity = QueryOutput<ApiClient['campaign']['capacity']>[number];

/**
 * D1: colar o link novo. Invalida o mesmo que `useRefreshGroupInvite` — o convite decide a
 * elegibilidade — e a fila de incidentes, que é de onde o gesto parte.
 */
export function usePasteGroupInvite() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateGroups();
  return useMutation(
    trpc.group.pasteInvite.mutationOptions({
      onSuccess: () => {
        invalidate();
        void queryClient.invalidateQueries({ queryKey: trpc.group.get.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.group.linkChecks.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.incident.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.incident.counts.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.redirector.nextTargets.queryKey() });
      },
    }),
  );
}

/** D2: as últimas checagens do convite do grupo. */
export function useGroupLinkChecks(workspaceId: string, groupId: string) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.group.linkChecks.queryOptions({ workspaceId, groupId }),
    enabled: groupId !== '',
    staleTime: 60_000,
  });
}

/** D5: as categorias do workspace, com o recorte já contado no servidor. */
export function useGroupCategories(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery(trpc.groupCategory.list.queryOptions({ workspaceId }));
}

function useInvalidateCategories() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const invalidateGroups = useInvalidateGroups();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.groupCategory.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.group.get.queryKey() });
    invalidateGroups();
  };
}

export function useCreateGroupCategory() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCategories();
  return useMutation(trpc.groupCategory.create.mutationOptions({ onSuccess: invalidate }));
}

export function useUpdateGroupCategory() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCategories();
  return useMutation(trpc.groupCategory.update.mutationOptions({ onSuccess: invalidate }));
}

export function useRemoveGroupCategory() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCategories();
  return useMutation(trpc.groupCategory.remove.mutationOptions({ onSuccess: invalidate }));
}

export function useSetGroupCategory() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCategories();
  return useMutation(trpc.group.setCategory.mutationOptions({ onSuccess: invalidate }));
}

/** D3: lotação agregada. Sem `campaignId` traz todas as campanhas do workspace. */
export function useCampaignCapacity(workspaceId: string, campaignId?: string) {
  const trpc = useTRPC();
  return useQuery(
    trpc.campaign.capacity.queryOptions({
      workspaceId,
      ...(campaignId ? { campaignId } : {}),
    }),
  );
}

/* ========================================================================== */
/*  Linha do tempo do grupo (Fase 3 — fatia final)                            */
/* ========================================================================== */

/**
 * CONTRATO REAL (conferido em `apps/api/src/modules/group/group.router.ts`):
 *
 *   group.get      { workspaceId, groupId, includeArchived? }  -> GroupRow
 *   group.timeline { workspaceId, groupId, limit?, cursor? }
 *                    -> { events, nextCursor, since }
 *   group.activity { workspaceId, groupId }
 *                    -> { since, facts, membership }
 *
 * Quatro coisas que a tela NÃO pode esquecer:
 *  - **`nextCursor` é texto opaco.** A tela devolve o que recebeu; não monta cursor, não
 *    manda `offset`. `participant_events` é particionada por mês e `offset` relê tudo que
 *    já passou.
 *  - **`source` não é decoração.** `WEBHOOK` é fato visto ao vivo; `SYNC` é fato
 *    descoberto na conferência. Dar a mesma cara aos dois faz o produto mentir sobre a
 *    própria precisão (ADR-0014).
 *  - **`occurredAtSource: 'receipt'`** quer dizer que o horário é o da nossa descoberta, e
 *    não o do fato. São precisões diferentes (invariante 9).
 *  - **`membership` nulo ≠ `membership.joinedAt` nulo.** O primeiro é "não há vínculo"
 *    (saída órfã); o segundo é "está no grupo desde antes do monitoramento". Nenhum dos
 *    dois é `0` (invariante 8).
 */

export type TimelinePage = QueryOutput<ApiClient['group']['timeline']>;
export type TimelineEvent = TimelinePage['events'][number];
export type GroupActivity = QueryOutput<ApiClient['group']['activity']>;

/**
 * UM grupo. `group.get` existe no router desde a Fase 3 e não tinha hook nenhum — a lista
 * servia a tudo. Uma tela de detalhe endereçada por URL não pode depender da lista: quem
 * abre o link direto, ou recarrega a página, não passou por ela.
 *
 * `includeArchived: true` sempre: o grupo que deixou de ser monitorado guarda o histórico,
 * e é nele que alguém vai querer olhar o que aconteceu antes de excluir.
 */
export function useGroup(workspaceId: string, groupId: string) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.group.get.queryOptions({ workspaceId, groupId, includeArchived: true }),
    enabled: groupId !== '',
  });
}

/**
 * A linha do tempo, paginada por cursor.
 *
 * `useInfiniteQuery` porque o gesto é "carregar mais", e não "ir para a página 3": a tabela
 * é append-only e cresce pela frente — numerar página faria a linha 26 mudar de página a
 * cada evento novo.
 */
export function useGroupTimeline(workspaceId: string, groupId: string, limit = 25) {
  const trpc = useTRPC();
  return useInfiniteQuery(
    trpc.group.timeline.infiniteQueryOptions(
      { workspaceId, groupId, limit },
      { getNextPageParam: (lastPage: TimelinePage) => lastPage.nextCursor ?? undefined },
    ),
  );
}

/** O resumo do cabeçalho. Consulta própria: não se refaz a cada "carregar mais". */
export function useGroupActivity(workspaceId: string, groupId: string) {
  const trpc = useTRPC();
  return useQuery(trpc.group.activity.queryOptions({ workspaceId, groupId }));
}

/**
 * O que uma sincronização de pessoas desloca NESTA tela.
 *
 * Existe como função única porque as três consultas mudam juntas e por um motivo só: a
 * varredura pode emitir fatos novos (linha do tempo), refazer a composição do grupo
 * (resumo) e destravar a exportação (prévia de contatos). Invalidar duas e esquecer a
 * terceira deixa a tela dizendo "nunca varrido" logo depois de varrer.
 */
export function useInvalidateGroupDetail() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    /*
     * `pathKey()` pelo mesmo motivo de `useInvalidateIncidents`: `group.timeline` é
     * `useInfiniteQuery`, e `queryKey()` sem input gera `{ type: 'query' }`, que nunca casa
     * com a chave `{ type: 'infinite' }` da lista paginada. Enquanto foi `queryKey()`, a
     * linha do tempo ficava com o retrato velho depois de sincronizar pessoas — as outras
     * três consultas desta função atualizavam e só ela não.
     */
    void queryClient.invalidateQueries({ queryKey: trpc.group.timeline.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.group.activity.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.group.contactsExport.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.group.get.queryKey() });
  };
}

/* ========================================================================== */
/*  Exportar contatos do grupo — o seguro contra o grupo ser banido           */
/* ========================================================================== */

/**
 * CONTRATO REAL:
 *
 *   group.contactsExport { workspaceId, groupId }
 *     -> { ready, participantsSyncedAt, total, withPhone }
 *
 *   GET /export/workspaces/:workspaceId/groups/:groupId/contacts.csv
 *     -> o arquivo, em lotes, com Content-Disposition
 *
 * O download **não** é tRPC de propósito: um envelope JSON carregaria o arquivo inteiro na
 * memória do processo e entregaria JSON ao navegador em vez de um download com nome. Quem
 * autentica é o cookie de sessão — por isso a URL tem de ser da MESMA origem (o dev server
 * do Vite proxia `/export`).
 *
 * `ready: false` quer dizer que o grupo nunca passou pela varredura completa: o arquivo
 * sairia quase vazio e isso só apareceria no dia do banimento. A tela diz isso e oferece
 * sincronizar antes; o servidor recusa o download de qualquer jeito.
 */
export type ContactsExportPreview = QueryOutput<ApiClient['group']['contactsExport']>;

export function useContactsExportPreview(workspaceId: string, groupId: string, enabled: boolean) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.group.contactsExport.queryOptions({ workspaceId, groupId }),
    /**
     * `enabled` desliga a consulta para quem não tem `group:export_contacts`: sem isso, todo
     * MEMBER que abrisse a tela levaria um 403 no console — e a tela pediria ao servidor
     * quantos telefones existem para quem não pode vê-los.
     */
    enabled: enabled && groupId !== '',
    staleTime: 0,
  });
}

/** O endereço do arquivo. Sempre relativo: cookie de sessão só viaja na mesma origem. */
export function contactsExportUrl(workspaceId: string, groupId: string): string {
  return `${API_ORIGIN}/export/workspaces/${workspaceId}/groups/${groupId}/contacts.csv`;
}

/* ========================================================================== */
/*  Campanhas (Fase 4)                                                        */
/* ========================================================================== */

/**
 * CONTRATO REAL (conferido em `apps/api/src/modules/campaigns/campaign.router.ts`):
 *
 *   campaign.list        { workspaceId, includeArchived? }
 *                          -> { campaigns, archivedCount, limit }
 *   campaign.get         { workspaceId, campaignId }              -> CampaignRow
 *   campaign.groups      { workspaceId, campaignId }              -> CampaignGroupRow[]
 *   campaign.create      { workspaceId, name, description? }      -> CampaignRow
 *   campaign.update      { workspaceId, campaignId, name?, description?, status?, position? }
 *                          -> CampaignRow
 *   campaign.archive     { workspaceId, campaignId }  -> { ok: true }
 *   campaign.unarchive   { workspaceId, campaignId }  -> CampaignRow
 *   campaign.remove      { workspaceId, campaignId }  -> { removed: true }  (exige arquivada)
 *   campaign.linkGroup   { workspaceId, campaignId, groupId } -> CampaignGroupRow[]
 *   campaign.unlinkGroup { workspaceId, campaignId, groupId } -> CampaignGroupRow[]
 *   campaign.meta        { workspaceId, campaignId } -> { link, suggestion, exposure }
 *   campaign.linkMeta    { workspaceId, campaignId, externalId, source } -> CampaignRow
 *   campaign.unlinkMeta  { workspaceId, campaignId }                     -> CampaignRow
 *   campaign.moveMeta    { workspaceId, externalId, toCampaignId }      -> CampaignRow   (09/10)
 *
 * Três coisas que a tela NÃO pode esquecer:
 *  - **`code` não se edita.** Não existe rota para isso, e não é esquecimento: o código já
 *    saiu do produto quando alguém o colou num anúncio.
 *  - **Arquivar ≠ excluir.** São dois botões, duas permissões e dois significados; excluir
 *    exige arquivar antes, e o servidor recusa o atalho.
 *  - `limit` chega AVALIADO. Refazer a conta aqui quebra a invariante 4.
 */

export type CampaignList = QueryOutput<ApiClient['campaign']['list']>;
export type Campaign = CampaignList['campaigns'][number];
/** Avaliação do limite de campanhas do plano — vem pronta do EntitlementService. */
export type CampaignLimit = CampaignList['limit'];
export type CampaignGroups = QueryOutput<ApiClient['campaign']['groups']>;
export type CampaignGroup = CampaignGroups[number];
export type CreateCampaignInput = MutationInput<ApiClient['campaign']['create']>;
export type UpdateCampaignInput = MutationInput<ApiClient['campaign']['update']>;
/** Ligação com o Meta + sugestão dos cliques + exposição atual da campanha. */
export type CampaignMeta = QueryOutput<ApiClient['campaign']['meta']>;
export type MetaSuggestion = CampaignMeta['suggestion']['suggestions'][number];
/** A escala de exposição ao dinheiro, já calculada no servidor (invariante 3). */
export type MoneyExposureView = CampaignMeta['exposure'];

export function useCampaigns(workspaceId: string, includeArchived = false) {
  const trpc = useTRPC();
  return useQuery(trpc.campaign.list.queryOptions({ workspaceId, includeArchived }));
}

/** Grupos de UMA campanha. `enabled` porque a gaveta só pede quando está aberta. */
export function useCampaignGroups(workspaceId: string, campaignId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.campaign.groups.queryOptions({ workspaceId, campaignId: campaignId ?? '' }),
    enabled: campaignId !== null,
  });
}

/**
 * Os grupos de VÁRIAS campanhas de uma vez.
 *
 * Existe para a lista de redirecionadores: cada link pertence a uma campanha, e a pergunta
 * "quantos grupos deste link estão prontos para receber gente?" só se responde com os grupos
 * DELE. Um `useCampaignGroups` por linha responderia o mesmo, mas a página inteira precisa do
 * conjunto para abrir o aviso no topo — e um hook por linha não sobe essa informação.
 *
 * Não custa uma consulta por linha: a chave é o `campaignId`, então dois links da mesma
 * campanha compartilham a mesma query, e o `httpBatchLink` junta o resto numa requisição só.
 */
export function useCampaignGroupsMany(workspaceId: string, campaignIds: readonly string[]) {
  const trpc = useTRPC();
  return useQueries({
    queries: campaignIds.map((campaignId) =>
      trpc.campaign.groups.queryOptions({ workspaceId, campaignId }),
    ),
    combine: (results) => ({
      /**
       * `undefined` no mapa é **não sabemos ainda**, e não "campanha sem grupo" — é o que
       * impede a tela de acusar "nenhum grupo pronto" enquanto a consulta está no ar
       * (invariante 8).
       */
      porCampanha: new Map(
        campaignIds.map((id, index) => [id, results[index]?.data as CampaignGroups | undefined]),
      ),
      isPending: results.some((result) => result.isPending),
    }),
  });
}

/**
 * Invalida o que uma escrita em campanha desloca: a lista (com o limite do plano) e os
 * grupos da campanha. A lista de GRUPOS não entra aqui — vincular não muda nada no grupo,
 * e invalidá-la à toa faria a tela de grupos recarregar sem motivo.
 */
function useInvalidateCampaigns() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.campaign.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.campaign.groups.queryKey() });
  };
}

export function useCreateCampaign() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaigns();
  return useMutation(trpc.campaign.create.mutationOptions({ onSuccess: invalidate }));
}

export function useUpdateCampaign() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaigns();
  return useMutation(trpc.campaign.update.mutationOptions({ onSuccess: invalidate }));
}

export function useArchiveCampaign() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaigns();
  return useMutation(trpc.campaign.archive.mutationOptions({ onSuccess: invalidate }));
}

export function useUnarchiveCampaign() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaigns();
  return useMutation(trpc.campaign.unarchive.mutationOptions({ onSuccess: invalidate }));
}

/** Excluir (soft delete no servidor). Exige a campanha já arquivada. */
export function useRemoveCampaign() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaigns();
  return useMutation(trpc.campaign.remove.mutationOptions({ onSuccess: invalidate }));
}

export function useLinkCampaignGroup() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaigns();
  return useMutation(trpc.campaign.linkGroup.mutationOptions({ onSuccess: invalidate }));
}

export function useUnlinkCampaignGroup() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaigns();
  return useMutation(trpc.campaign.unlinkGroup.mutationOptions({ onSuccess: invalidate }));
}

/**
 * A ligação com o Meta Ads de UMA campanha, com a sugestão e a exposição.
 *
 * `enabled` porque só a gaveta pede: a sugestão varre `redirect_clicks` de sete dias, e
 * pagá-la para todas as campanhas da lista seria uma varredura por linha sem ninguém olhando.
 */
export function useCampaignMeta(workspaceId: string, campaignId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.campaign.meta.queryOptions({ workspaceId, campaignId: campaignId ?? '' }),
    enabled: campaignId !== null,
  });
}

/**
 * Invalida o que uma mudança de ligação com o Meta desloca.
 *
 * Além da lista (que mostra o selo "Meta") e da própria gaveta, **a fila de incidentes entra
 * aqui**: declarar um anúncio muda a severidade dos incidentes daquela campanha na varredura
 * seguinte, e quem acabou de vincular precisa ver isso sem recarregar a página na mão.
 *
 * `pathKey()` em `incident.list`, e NÃO `queryKey()`: ela é `useInfiniteQuery`, e
 * `queryKey()` sem input monta `{ type: 'query' }`, que nunca casa com a chave
 * `{ type: 'infinite' }` da lista paginada.
 */
function useInvalidateCampaignMeta() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const invalidateCampaigns = useInvalidateCampaigns();
  return () => {
    invalidateCampaigns();
    void queryClient.invalidateQueries({ queryKey: trpc.campaign.meta.pathKey() });
    // O aviso do topo e o custo do bloco mudam com o vínculo (08/10).
    void queryClient.invalidateQueries({ queryKey: trpc.campaign.metaSemVinculo.pathKey() });
    // A tela Meta Ads mostra "→ bloco X" a partir das entidades (09/10).
    void queryClient.invalidateQueries({ queryKey: trpc.ads.entities.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.analytics.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.incident.list.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.incident.counts.queryKey() });
  };
}

export function useLinkCampaignMeta() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaignMeta();
  return useMutation(trpc.campaign.linkMeta.mutationOptions({ onSuccess: invalidate }));
}

/**
 * Campanhas da Meta mandando gente (ou gastando) sem vínculo — o aviso do topo (08/10). Só
 * para quem pode vincular (`campaign:update`); a cada 5 min, que é metade da leitura da Meta.
 */
export function useMetaSemVinculo(workspaceId: string, enabled: boolean) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.campaign.metaSemVinculo.queryOptions({ workspaceId }),
    enabled,
    refetchInterval: 5 * 60_000,
  });
}

/** "Não manda gente para os grupos" — some do aviso para sempre. */
export function useDispensarMeta() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.campaign.dispensarMeta.mutationOptions({
      onSuccess: () =>
        void queryClient.invalidateQueries({ queryKey: trpc.campaign.metaSemVinculo.pathKey() }),
    }),
  );
}

export function useUnlinkCampaignMeta() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaignMeta();
  return useMutation(trpc.campaign.unlinkMeta.mutationOptions({ onSuccess: invalidate }));
}

/** Troca a campanha da Meta de bloco numa escrita só (09/10) — ver `moveMetaCampaign`. */
export function useMoveCampaignMeta() {
  const trpc = useTRPC();
  const invalidate = useInvalidateCampaignMeta();
  return useMutation(trpc.campaign.moveMeta.mutationOptions({ onSuccess: invalidate }));
}

/* ========================================================================== */
/*  Redirecionadores (Fase 5)                                                 */
/* ========================================================================== */

/**
 * CONTRATO REAL (conferido em `apps/api/src/modules/redirector/redirector.router.ts`):
 *
 *   redirector.list        { workspaceId, includeArchived? }
 *                            -> { redirectors, archivedCount, limit }
 *   redirector.get         { workspaceId, redirectorId }            -> RedirectorRow
 *   redirector.clicks      { workspaceId, redirectorId, limit?, before? } -> ClickRow[]
 *   redirector.nextTargets { workspaceId, redirectorId }            -> NextTargetRow[]
 *   redirector.create      { workspaceId, name, campaignId, slug?, fallbackUrlId?,
 *                            attributionWindowMinutes? }            -> RedirectorRow
 *   redirector.update      { workspaceId, redirectorId, name?, campaignId?, isActive?,
 *                            fallbackUrlId?, attributionWindowMinutes?, position? }
 *                                                                   -> RedirectorRow
 *   redirector.archive     { workspaceId, redirectorId }  -> { ok: true }
 *   redirector.unarchive   { workspaceId, redirectorId }  -> RedirectorRow
 *   redirector.remove      { workspaceId, redirectorId }  -> { removed: true } (exige arquivado)
 *   redirector.allowlist       { workspaceId }                      -> AllowlistRow[]
 *   redirector.allowlistAdd    { workspaceId, url, label }          -> AllowlistRow[]
 *   redirector.allowlistRemove { workspaceId, allowlistUrlId }      -> AllowlistRow[]
 *
 * Cinco coisas que a tela NÃO pode esquecer:
 *  - **`slug` não se edita.** Não existe rota para isso, e não é esquecimento: o endereço
 *    já saiu do produto no instante em que alguém o colou num anúncio.
 *  - **Não existe rota que aceite URL de destino solta** (invariante 7). O fallback é
 *    escolhido por `id` dentro da allowlist, e a URL só entra nela por `allowlistAdd`.
 *  - **`strategy` VIROU escolha** (migration 0013). São três — `FILL_SEQUENTIAL`, `RANDOM` e
 *    `SPECIFIC_GROUP` —, e `create`/`update` aceitam `strategy` + `pinnedGroupId`. A regra
 *    que não mudou: `SPECIFIC_GROUP` exige o grupo fixo, as outras exigem que ele seja nulo,
 *    e o grupo fixado tem que estar vinculado à campanha DO LINK — quem cobra isso, com
 *    frase de gente, é o servidor (`assertStrategyCoherent`).
 *  - **`nextTargets` mudou de significado.** Cada linha traz `strategy` e `isPinned`, e
 *    `isNext` quer dizer "pode receber o próximo clique": UM no sequencial, só o fixado no
 *    grupo fixo, e TODOS os que cabem no sorteio. Uma tela que continue dizendo "o próximo
 *    vai para X" com sorteio ligado estará mentindo.
 *  - **`nextTargets` NÃO traz o convite do grupo**, e o convite é o que decide se o grupo
 *    recebe alguém (`invite_code is not null` no allocator). Quem traz é `group.list`. Ver
 *    `avaliarEntrega` em `features/redirector/redirector-ui.ts`.
 *  - `clicksTotal`/`clicksNoOrigin` são a vida INTEIRA do link. Para o recorte por PERÍODO
 *    existe `redirector.originSummary` (Fase 6) — e é ele que a tela usa no diagnóstico,
 *    porque o acumulado de vida inteira some justamente com a melhora: quem consertou o
 *    botão da landing page na terça continuaria vendo "280 sem origem" na sexta.
 *    `lastClickAt === null` é "nunca recebeu clique", diferente de `clicksTotal === 0`
 *    medido (invariante 8).
 *  - **`publicUrl` vem PRONTO do servidor** (Fase 6). A tela não monta mais o endereço a
 *    partir de `VITE_REDIRECT_BASE_URL`: o servidor conhece `REDIRECT_BASE_URL` e é ele
 *    quem sabe onde o redirecionador mora. Dois lugares sabendo o mesmo endereço, sendo que
 *    esse endereço vai para dentro de um anúncio pago, é como um deles fica errado.
 *  - `limit` chega AVALIADO. Refazer a conta aqui quebra a invariante 4.
 */

export type RedirectorList = QueryOutput<ApiClient['redirector']['list']>;
export type Redirector = RedirectorList['redirectors'][number];
/** Avaliação do limite de redirecionadores do plano — vem pronta do EntitlementService. */
export type RedirectorLimit = RedirectorList['limit'];
export type RedirectorClick = QueryOutput<ApiClient['redirector']['clicks']>[number];
export type RedirectorTarget = QueryOutput<ApiClient['redirector']['nextTargets']>[number];
export type RedirectorAllowlist = QueryOutput<ApiClient['redirector']['allowlist']>;
export type RedirectorAllowlistUrl = RedirectorAllowlist[number];
export type CreateRedirectorInput = MutationInput<ApiClient['redirector']['create']>;
export type UpdateRedirectorInput = MutationInput<ApiClient['redirector']['update']>;

export function useRedirectors(workspaceId: string, includeArchived = false) {
  const trpc = useTRPC();
  return useQuery(trpc.redirector.list.queryOptions({ workspaceId, includeArchived }));
}

/**
 * Os cliques recentes de UM link — a auditoria do allocator na tela.
 *
 * `enabled` porque a gaveta só pede quando está aberta, e `staleTime: 0` porque esta lista é
 * o que alguém abre justamente para ver o que acabou de acontecer: mostrar o retrato da vez
 * passada responderia "nada mudou" a quem está conferindo se mudou.
 */
export interface ClickFilters {
  /** `undefined` = todas as origens. */
  origin?: RedirectorClick['origin'];
  /** `undefined` = todos os desfechos. */
  status?: RedirectorClick['status'];
}

export function useRedirectorClicks(
  workspaceId: string,
  redirectorId: string | null,
  limit = 50,
  filters: ClickFilters = {},
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.clicks.queryOptions({
      workspaceId,
      redirectorId: redirectorId ?? '',
      limit,
      /*
       * O filtro vai para o SERVIDOR, e não é um `.filter()` nesta lista: filtrar depois de
       * paginar devolveria "nenhum sem origem" numa página de 50 que por acaso não tinha
       * nenhum — e a conclusão errada seria "o link está bom".
       */
      ...(filters.origin === undefined ? {} : { origin: filters.origin }),
      ...(filters.status === undefined ? {} : { status: filters.status }),
    }),
    enabled: redirectorId !== null,
    staleTime: 0,
  });
}

export type OriginSummary = QueryOutput<ApiClient['redirector']['originSummary']>;

/**
 * Cliques por origem **no período**, contados no servidor (§3 de `metricas.md`).
 *
 * `redirectorId: null` = o workspace inteiro (o painel de diagnóstico no topo da tela).
 * `from`/`to` são instantes; `to` é exclusivo.
 */
export function useOriginSummary(
  workspaceId: string,
  window: { from: Date; to: Date },
  redirectorId: string | null = null,
) {
  const trpc = useTRPC();
  return useQuery(
    trpc.redirector.originSummary.queryOptions({
      workspaceId,
      from: window.from,
      to: window.to,
      ...(redirectorId === null ? {} : { redirectorId }),
    }),
  );
}

/**
 * "Para onde vai o próximo clique?" — retrato, não decisão: o servidor não reserva nada aqui.
 * Fica desatualizado no instante seguinte, e isso é honesto, porque ficaria de qualquer jeito.
 */
export function useRedirectorTargets(workspaceId: string, redirectorId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.nextTargets.queryOptions({ workspaceId, redirectorId: redirectorId ?? '' }),
    enabled: redirectorId !== null,
    staleTime: 0,
  });
}

/** A allowlist de destinos de reserva. É daqui que sai TODO fallback (invariante 7). */
export type RedirectorPreviewHits = QueryOutput<ApiClient['redirector']['previewHits']>;

/**
 * A1 — prévias de robô (WhatsApp, Facebook…) dos últimos `days` dias do workspace.
 *
 * Não é clique: a tela mostra AO LADO dos cliques, nunca somado. O recorte em dias do
 * workspace é resolvido no servidor (o painel só manda o número de dias).
 */
export function useRedirectorPreviewHits(
  workspaceId: string,
  redirectorId: string | null,
  days = 30,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.previewHits.queryOptions({
      workspaceId,
      redirectorId: redirectorId ?? '',
      days,
    }),
    enabled: redirectorId !== null,
  });
}

export type RedirectorTestLink = MutationOutput<ApiClient['redirector']['testLink']>;

/** B1 — gera o link de teste (15 min). Não invalida nada: nada muda no servidor além da auditoria. */
export function useCreateRedirectorTestLink() {
  const trpc = useTRPC();
  return useMutation(trpc.redirector.testLink.mutationOptions());
}

/* ---------------------------------------------------------------- B2–B4 -- */

export type RedirectorPage = QueryOutput<ApiClient['redirector']['page']>;
export type RedirectorPageUpdateInput = MutationInput<ApiClient['redirector']['pageUpdate']>;
export type RedirectorPageVariants = QueryOutput<ApiClient['redirector']['pageVariants']>;
export type RedirectorPageVariantRow = RedirectorPageVariants['page'];

/**
 * B2–B4 — a página de passagem do link (configuração e estado da imagem, nunca os bytes).
 * Todo membro lê; só OWNER/ADMIN escreve (`redirector:page`, cobrado no servidor).
 */
export function useRedirectorPage(workspaceId: string, redirectorId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.page.queryOptions({ workspaceId, redirectorId: redirectorId ?? '' }),
    enabled: redirectorId !== null,
  });
}

/**
 * B2.6 — o placar PAGE × DIRECT, por coorte de clique, nos últimos `days` dias do workspace.
 * Taxa e diferença vêm prontas do servidor (invariante 3).
 */
export function useRedirectorPageVariants(
  workspaceId: string,
  redirectorId: string | null,
  days = 30,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.pageVariants.queryOptions({
      workspaceId,
      redirectorId: redirectorId ?? '',
      days,
    }),
    enabled: redirectorId !== null,
  });
}

/**
 * A imagem da prévia como `data:` URL (a CSP do painel não carrega imagem do host do
 * redirecionador). `enabled` só quando há imagem gravada.
 */
export function useRedirectorPageImage(
  workspaceId: string,
  redirectorId: string | null,
  hasImage: boolean,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.redirector.pageImage.queryOptions({ workspaceId, redirectorId: redirectorId ?? '' }),
    enabled: redirectorId !== null && hasImage,
  });
}

/**
 * Toda escrita na página devolve a configuração nova: grava direto no cache da consulta, e a
 * imagem da tela é refeita (ela pode ter mudado).
 */
function useSetRedirectorPageCache() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return (workspaceId: string, page: RedirectorPage) => {
    queryClient.setQueryData(
      trpc.redirector.page.queryKey({ workspaceId, redirectorId: page.redirectorId }),
      page,
    );
    void queryClient.invalidateQueries({
      queryKey: trpc.redirector.pageImage.queryKey({
        workspaceId,
        redirectorId: page.redirectorId,
      }),
    });
  };
}

export function useUpdateRedirectorPage() {
  const trpc = useTRPC();
  const setCache = useSetRedirectorPageCache();
  return useMutation(
    trpc.redirector.pageUpdate.mutationOptions({
      onSuccess: (page, input) => setCache(input.workspaceId, page),
    }),
  );
}

export function useUploadRedirectorPageImage() {
  const trpc = useTRPC();
  const setCache = useSetRedirectorPageCache();
  return useMutation(
    trpc.redirector.pageImageUpload.mutationOptions({
      onSuccess: (page, input) => setCache(input.workspaceId, page),
    }),
  );
}

export function useGenerateRedirectorPageImage() {
  const trpc = useTRPC();
  const setCache = useSetRedirectorPageCache();
  return useMutation(
    trpc.redirector.pageImageGenerate.mutationOptions({
      onSuccess: (page, input) => setCache(input.workspaceId, page),
    }),
  );
}

export function useRemoveRedirectorPageImage() {
  const trpc = useTRPC();
  const setCache = useSetRedirectorPageCache();
  return useMutation(
    trpc.redirector.pageImageRemove.mutationOptions({
      onSuccess: (page, input) => setCache(input.workspaceId, page),
    }),
  );
}

export function useRedirectorAllowlist(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery(trpc.redirector.allowlist.queryOptions({ workspaceId }));
}

/**
 * Invalida o que uma escrita em redirecionador desloca: a lista (com o limite do plano) e a
 * prévia de alocação — repontar o link para outra campanha troca a fila de grupos inteira.
 * A lista de CLIQUES não entra: ela é append-only e não muda por configuração.
 */
function useInvalidateRedirectors() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.redirector.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.redirector.nextTargets.queryKey() });
  };
}

export function useCreateRedirector() {
  const trpc = useTRPC();
  const invalidate = useInvalidateRedirectors();
  return useMutation(trpc.redirector.create.mutationOptions({ onSuccess: invalidate }));
}

export function useUpdateRedirector() {
  const trpc = useTRPC();
  const invalidate = useInvalidateRedirectors();
  return useMutation(trpc.redirector.update.mutationOptions({ onSuccess: invalidate }));
}

export function useArchiveRedirector() {
  const trpc = useTRPC();
  const invalidate = useInvalidateRedirectors();
  return useMutation(trpc.redirector.archive.mutationOptions({ onSuccess: invalidate }));
}

export function useUnarchiveRedirector() {
  const trpc = useTRPC();
  const invalidate = useInvalidateRedirectors();
  return useMutation(trpc.redirector.unarchive.mutationOptions({ onSuccess: invalidate }));
}

/** Excluir (soft delete no servidor). Exige o link já arquivado, e o slug nunca volta. */
export function useRemoveRedirector() {
  const trpc = useTRPC();
  const invalidate = useInvalidateRedirectors();
  return useMutation(trpc.redirector.remove.mutationOptions({ onSuccess: invalidate }));
}

/** Invalida a allowlist e a lista: o rótulo do destino de reserva aparece na linha do link. */
function useInvalidateAllowlist() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.redirector.allowlist.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.redirector.list.queryKey() });
  };
}

export function useAddAllowlistUrl() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAllowlist();
  return useMutation(trpc.redirector.allowlistAdd.mutationOptions({ onSuccess: invalidate }));
}

export function useRemoveAllowlistUrl() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAllowlist();
  return useMutation(trpc.redirector.allowlistRemove.mutationOptions({ onSuccess: invalidate }));
}

/* ========================================================================== */
/*  Analytics (Fase 6)                                                         */
/* ========================================================================== */

/**
 * O painel.
 *
 *   analytics.overview  { workspaceId, period? } -> KPIs + série + atribuição + permanência
 *   analytics.hourly    { workspaceId, period? } -> 24 faixas, no fuso do workspace
 *   analytics.activity  { workspaceId }          -> intensidade diária dos últimos 365 dias
 *   analytics.cohorts   { workspaceId, period? } -> mapa de coortes (alias de retention.matrix)
 *   analytics.breakdown { workspaceId, period? } -> quebra por grupo e por campanha
 *
 * **Três coisas que a tela NÃO faz**, e que valem mais que qualquer comentário sobre tipos:
 *
 *  1. **Não calcula métrica** (invariante 3). Saldo, cobertura, evasão, mediana de
 *     permanência, variação contra o período anterior e as faixas de percentil chegam
 *     prontas. Não existe uma soma sequer nas telas de `pages/workspace/dashboard/`.
 *  2. **Não decide o período.** Ela manda o NOME (`HOJE`, `D7`, `D30`, `D90`) e o servidor
 *     resolve no fuso do workspace, com o relógio dele. "Hoje" às 22h em São Paulo já é
 *     amanhã em Lisboa; se o recorte saísse daqui, duas pessoas do mesmo time veriam dois
 *     "hoje" diferentes e a segunda só descobriria comparando números com a primeira.
 *  3. **Não transforma `null` em `0`.** Todo `null` neste contrato quer dizer "não há o que
 *     medir" — cobertura sem entrada, taxa sem clique, evasão sem contagem, marco de coorte
 *     que ainda não venceu, mediana sem nenhuma permanência conhecida. Cada um vira
 *     travessão **com a frase que explica o que falta acontecer**, nunca zero.
 */

export type PeriodPreset = 'HOJE' | 'D7' | 'D30' | 'D90';
/** Um preset OU um intervalo de datas do negócio (`YYYY-MM-DD`, inclusive) — 06/10. */
export type PeriodArg = PeriodPreset | { from: string; to: string };

function periodInput(p: PeriodArg): { preset: PeriodPreset } | { from: string; to: string } {
  return typeof p === 'string' ? { preset: p } : { from: p.from, to: p.to };
}

export type AnalyticsOverview = QueryOutput<ApiClient['analytics']['overview']>;
export type AnalyticsPeriod = AnalyticsOverview['period'];
export type AnalyticsSeriesPoint = AnalyticsOverview['series'][number];
export type AnalyticsTotals = AnalyticsOverview['totals'];
export type AnalyticsAttribution = AnalyticsOverview['attribution'];
export type AnalyticsTenure = AnalyticsOverview['tenure'];
export type AnalyticsObserved = AnalyticsOverview['observed'];
export type AnalyticsHourly = QueryOutput<ApiClient['analytics']['hourly']>;
export type AnalyticsActivity = QueryOutput<ApiClient['analytics']['activity']>;
export type AnalyticsCohorts = QueryOutput<ApiClient['analytics']['cohorts']>;
export type AnalyticsCohortRow = AnalyticsCohorts['rows'][number];
export type AnalyticsBreakdown = QueryOutput<ApiClient['analytics']['breakdown']>;
export type AnalyticsGroupRow = AnalyticsBreakdown['groups'][number];
export type AnalyticsCampaignRow = AnalyticsBreakdown['campaigns'][number];
/** Uma linha do funil `visitas na LP → cliques → entradas` + evasão do mesmo dia. */
export type AnalyticsFunnelRow = AnalyticsBreakdown['funil'][number];

/**
 * Painel vivo (pedido do dono, 06/10): os números se atualizam sozinhos a cada minuto e ao
 * voltar para a aba. O gasto da Meta chega pelo sync de 10 em 10 min; isto só garante que a
 * tela não fica parada em cima de um número velho.
 */
const AO_VIVO = { refetchInterval: 60_000, refetchOnWindowFocus: true } as const;

export function useAnalyticsOverview(workspaceId: string, preset: PeriodArg) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.analytics.overview.queryOptions({ workspaceId, period: periodInput(preset) }),
    ...AO_VIVO,
  });
}

export function useAnalyticsHourly(workspaceId: string, preset: PeriodArg) {
  const trpc = useTRPC();
  return useQuery(trpc.analytics.hourly.queryOptions({ workspaceId, period: periodInput(preset) }));
}

/**
 * A atividade do ano. Sem período de propósito: é o mapa do ano inteiro, e recortá-lo em 7
 * dias devolveria uma tira de sete casas que não diz nada.
 *
 * `staleTime` maior que o padrão: 365 dias não mudam de forma a cada minuto, e esta é a única
 * leitura do painel que passa por um rollup (as outras leem o histórico direto).
 */
export function useAnalyticsActivity(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.analytics.activity.queryOptions({ workspaceId }),
    staleTime: 5 * 60_000,
  });
}

export function useAnalyticsCohorts(workspaceId: string, preset: PeriodArg) {
  const trpc = useTRPC();
  return useQuery(
    trpc.analytics.cohorts.queryOptions({ workspaceId, period: periodInput(preset) }),
  );
}

/* ------------------------------------------------------------------------ *
 *  Curva de Evasão (ADR-0019) — analytics.retention.*                       *
 *  Os números chegam prontos: Taxa, faixa, semáforo e a frase do card.      *
 * ------------------------------------------------------------------------ */

export type RetentionSummary = QueryOutput<ApiClient['analytics']['retention']['summary']>;
export type RetentionTaxa = RetentionSummary['atual'][0];
export type RetentionCurve = QueryOutput<ApiClient['analytics']['retention']['curve']>;
export type RetentionCurveSeries = RetentionCurve['series'][number];
export type RetentionRecorte = RetentionCurveSeries['recorte'];
export type RetentionMatrix = QueryOutput<ApiClient['analytics']['retention']['matrix']>;
export type RetentionBreakdown = QueryOutput<ApiClient['analytics']['retention']['breakdown']>;
export type RetentionBreakdownRow = RetentionBreakdown['rows'][number];
export type RetentionDimensao = 'CAMPAIGN' | 'META_CAMPAIGN' | 'GROUP' | 'META_AD' | 'ORIGIN';

/** O card "Evasão" do Dashboard. */
export function useRetentionSummary(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery(trpc.analytics.retention.summary.queryOptions({ workspaceId }));
}

/** A curva de sobrevivência — 1 a 3 recortes lado a lado. */
export function useRetentionCurve(
  workspaceId: string,
  preset: PeriodArg,
  series: RetentionRecorte[],
  enabled = true,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.analytics.retention.curve.queryOptions({
      workspaceId,
      period: periodInput(preset),
      series,
    }),
    enabled: enabled && series.length > 0,
  });
}

/** O mapa de coortes (Histórico). */
export function useRetentionMatrix(workspaceId: string, preset: PeriodArg) {
  const trpc = useTRPC();
  return useQuery(
    trpc.analytics.retention.matrix.queryOptions({ workspaceId, period: periodInput(preset) }),
  );
}

/** A quebra por anúncio / campanha da Meta / grupo / origem / bloco. */
export function useRetentionBreakdown(
  workspaceId: string,
  preset: PeriodArg,
  por: RetentionDimensao,
) {
  const trpc = useTRPC();
  return useQuery(
    trpc.analytics.retention.breakdown.queryOptions({
      workspaceId,
      period: periodInput(preset),
      por,
    }),
  );
}

/* ------------------------------------------------------------------------ *
 *  G3/G4 — metas mensais com semáforo e as até 3 recomendações do dia.      *
 *  Número, meta, cor, dica e frase chegam prontos do servidor.              *
 * ------------------------------------------------------------------------ */

export type GoalsOverview = QueryOutput<ApiClient['goals']['overview']>;
export type CampaignGoalStatus = GoalsOverview['campanhas'][number];
export type GoalIndicator = CampaignGoalStatus['indicadores'][number];
export type DailyRecommendations = QueryOutput<ApiClient['goals']['recommendations']>;
export type DailyRecommendation = DailyRecommendations['itens'][number];

/** O semáforo de cada bloco (Configurações → Metas, Gestão, cards). */
export function useGoalsOverview(workspaceId: string, enabled = true) {
  const trpc = useTRPC();
  return useQuery({ ...trpc.goals.overview.queryOptions({ workspaceId }), enabled });
}

/**
 * Grava (ou limpa) a meta do mês do bloco. A meta muda a cor de números de várias telas:
 * invalida o semáforo, as recomendações e a retenção/funil que usam a meta no lugar da mediana.
 */
export function useSetGoal() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.goals.set.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.goals.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.analytics.pathKey() });
      },
    }),
  );
}

/** "O que fazer hoje" — até 3, com a contagem que sustenta cada uma. */
export function useDailyRecommendations(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery(trpc.goals.recommendations.queryOptions({ workspaceId }));
}

/** "Dispensar" — esconde hoje, para o time. Não executa nada (invariante 13). */
export function useDismissRecommendation() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.goals.dismiss.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.goals.recommendations.queryKey() });
      },
    }),
  );
}

export type TodayData = QueryOutput<ApiClient['analytics']['today']>;

/** O retrato de HOJE do Dashboard (06/10). Atualiza sozinho a cada minuto. */
export function useAnalyticsToday(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.analytics.today.queryOptions({ workspaceId }),
    refetchInterval: 60_000,
  });
}

export type CampaignCardsData = QueryOutput<ApiClient['analytics']['campaignCards']>;
export type CampaignCardData = CampaignCardsData['campanhas'][number];

/** Os cards da lista de Campanhas (G5): o dia de hoje de cada campanha. Atualiza a cada minuto. */
export function useCampaignCards(workspaceId: string, enabled = true) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.analytics.campaignCards.queryOptions({ workspaceId }),
    enabled,
    refetchInterval: 60_000,
  });
}

export type CampaignDetailData = QueryOutput<ApiClient['analytics']['campaign']>;

/** A página de UMA campanha: funil com custo, série no tempo e grupos (06/10). */
export function useCampaignDetail(workspaceId: string, campaignId: string, preset: PeriodArg) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.analytics.campaign.queryOptions({
      workspaceId,
      campaignId,
      period: periodInput(preset),
    }),
    ...AO_VIVO,
  });
}

export function useAnalyticsBreakdown(workspaceId: string, preset: PeriodArg) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.analytics.breakdown.queryOptions({ workspaceId, period: periodInput(preset) }),
    ...AO_VIVO,
  });
}

/* ========================================================================== */
/*  Relatórios (Fase 9)                                                        */
/* ========================================================================== */

/**
 * CONTRATO REAL (`apps/api/src/modules/report/report.router.ts` e `report.routes.ts`):
 *
 *   report.preview   { workspaceId, period?, campaignId? } -> { report, filename }
 *   report.campaigns { workspaceId }                       -> [{ id, name, code }]
 *
 *   GET /export/workspaces/:workspaceId/relatorio.csv?periodo=&campanha=
 *   GET /export/workspaces/:workspaceId/relatorio.html?periodo=&campanha=
 *
 * **A prévia é o mesmo objeto que vira arquivo.** `report.preview` devolve exatamente o que
 * `buildWorkspaceReport` montou, e o CSV e o PDF são feitos a partir dele — não há um
 * caminho "para a tela" e outro "para o download". Prévia e arquivo que se calculam
 * separado é como a tela promete uma coisa e o arquivo entrega outra, e ninguém compara os
 * dois até o dia em que o número importa.
 *
 * Os downloads NÃO são tRPC: um envelope JSON entregaria o arquivo como texto dentro de um
 * objeto, sem `Content-Disposition` e sem nome. Quem autentica é o cookie de sessão, então
 * as URLs são da MESMA origem (o dev server do Vite proxia `/export`).
 *
 * Nada aqui calcula (invariante 3). A tela FORMATA linhas prontas — inclusive o grau de
 * confiança, que chega como um dos quatro valores declarados pelo servidor.
 */

export type ReportPreview = QueryOutput<ApiClient['report']['preview']>;
export type ReportDocument = ReportPreview['report'];
export type ReportRow = ReportDocument['rows'][number];
export type ReportConfidence = NonNullable<ReportRow['confidence']>;
export type ReportCampaignOption = QueryOutput<ApiClient['report']['campaigns']>[number];

export function useReportPreview(
  workspaceId: string,
  preset: PeriodPreset,
  campaignId: string | null,
  enabled: boolean,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.report.preview.queryOptions({
      workspaceId,
      period: { preset },
      campaignId,
    }),
    /**
     * Desligado para quem não tem `analytics:export`: sem isto, todo MEMBER que abrisse a
     * tela levaria um 403 no console. Esconder é UX; quem barra continua sendo o servidor.
     */
    enabled,
  });
}

export function useReportCampaigns(workspaceId: string, enabled: boolean) {
  const trpc = useTRPC();
  return useQuery({ ...trpc.report.campaigns.queryOptions({ workspaceId }), enabled });
}

/**
 * O endereço dos arquivos. Sempre relativo à origem do app: o cookie de sessão só viaja
 * assim, e é ele quem autentica o download.
 */
function reportUrl(
  extensao: 'csv' | 'html',
  workspaceId: string,
  preset: PeriodPreset,
  campaignId: string | null,
): string {
  const query = new URLSearchParams({ periodo: preset });
  if (campaignId !== null) query.set('campanha', campaignId);
  return `${API_ORIGIN}/export/workspaces/${workspaceId}/relatorio.${extensao}?${query.toString()}`;
}

export function reportCsvUrl(
  workspaceId: string,
  preset: PeriodPreset,
  campaignId: string | null,
): string {
  return reportUrl('csv', workspaceId, preset, campaignId);
}

/**
 * O documento de impressão. Abre em aba nova e chama a caixa de impressão sozinho — é de lá
 * que sai o PDF. Ver `modules/report/report-pdf.ts` na api: as cores são SVG porque o Chrome
 * não imprime fundo de CSS.
 */
export function reportPdfUrl(
  workspaceId: string,
  preset: PeriodPreset,
  campaignId: string | null,
): string {
  return reportUrl('html', workspaceId, preset, campaignId);
}

/* ========================================================================== */
/*  Incidentes (Fase 7)                                                       */
/* ========================================================================== */

/**
 * CONTRATO (Fase 7 — `apps/api/src/modules/incident/incident.router.ts`):
 *
 *   incident.list        { workspaceId, status?, limit?, cursor? }
 *                          -> { incidents, nextCursor }
 *   incident.counts      { workspaceId }
 *                          -> { open, acknowledged, critical }
 *   incident.acknowledge { workspaceId, incidentId }  -> Incident
 *   incident.resolve     { workspaceId, incidentId }  -> Incident
 *
 * Quatro coisas que a tela NÃO pode esquecer:
 *  - **Reconhecer não resolve.** São duas mutações, duas colunas (`acknowledgedAt` e
 *    `resolvedAt`) e dois significados. Tratar as duas como "sumir da lista" é o jeito
 *    de alguém reconhecer tudo achando que consertou.
 *  - **`status` vai para o SERVIDOR**, não é `.filter()` nesta lista: filtrar depois de
 *    paginar devolveria "nenhum aberto" numa página de 20 que por acaso só tinha
 *    resolvidos — e a conclusão errada seria "está tudo bem".
 *  - **`nextCursor` é texto opaco.** A tela devolve o que recebeu; não monta cursor e não
 *    manda `offset` (a tabela cresce pela frente, `offset` relê o que já passou).
 *  - **Lista vazia ≠ lista que não chegou** (invariante 8). Enquanto `counts` não
 *    responde, o menu não mostra `0`: dizer "nenhum incidente" sem ter perguntado é o
 *    painel afirmando que está tudo bem.
 */

/**
 * A ENTRADA de um procedure de leitura. `MutationInput` já existia; a listagem de
 * incidentes é a primeira query cujo parâmetro (`status`) a tela precisa tipar, e ele tem
 * que sair do router pelo mesmo motivo de todo o resto deste arquivo: se o servidor
 * ganhar um estado novo, o erro aparece aqui e não numa aba que volta vazia.
 */
type QueryInput<TProcedure extends { query: (input: never, ...rest: never[]) => unknown }> =
  Parameters<TProcedure['query']>[0];

export type IncidentPage = QueryOutput<ApiClient['incident']['list']>;
export type Incident = IncidentPage['incidents'][number];
export type IncidentCounts = QueryOutput<ApiClient['incident']['counts']>;
/** `'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'ALL'` — o filtro da tela, vindo do router. */
export type IncidentStatusFilter = NonNullable<QueryInput<ApiClient['incident']['list']>['status']>;
export type IncidentRefInput = MutationInput<ApiClient['incident']['acknowledge']>;

/**
 * A lista, paginada por cursor.
 *
 * `useInfiniteQuery` porque o gesto é "carregar mais", e não "ir para a página 3": a lista
 * é append-only e cresce pela frente — numerar página faria o incidente 21 mudar de página
 * a cada detecção nova.
 */
export function useIncidents(workspaceId: string, status: IncidentStatusFilter, limit = 20) {
  const trpc = useTRPC();
  return useInfiniteQuery(
    trpc.incident.list.infiniteQueryOptions(
      { workspaceId, status, limit },
      { getNextPageParam: (lastPage: IncidentPage) => lastPage.nextCursor ?? undefined },
    ),
  );
}

/**
 * As três contagens que alimentam o contador do menu e o resumo do topo.
 *
 * Consulta própria, e não um `.length` da lista: a lista vem paginada E filtrada, então
 * contar o que está na tela diria "3 abertos" numa página de 20 que mostra os 3 primeiros
 * de 47. Contador não é fonte da verdade (invariante 5) — quem conta é o servidor.
 *
 * `workspaceId` nulo desliga: o layout resolve o slug depois de montar, e uma consulta
 * disparada com id vazio volta 400 em toda navegação.
 */
export function useIncidentCounts(workspaceId: string | null, enabled = true) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.incident.counts.queryOptions({ workspaceId: workspaceId ?? '' }),
    enabled: enabled && workspaceId !== null && workspaceId !== '',
  });
}

/**
 * Invalida o que uma escrita em incidente desloca: a lista (o item troca de aba) e as
 * contagens (o contador do menu cai). Invalidar só a lista deixaria o menu dizendo "3"
 * com a tela mostrando 2 — e o número do menu é o que a pessoa olha de longe.
 *
 * Exportada porque não é só a mutação desta aba que desloca isso: um incidente aberto pelo
 * servidor chega pelo stream de tempo real (`lib/realtime.ts`), e quem receber esse evento
 * precisa do MESMO par de chaves. Duas listas de "o que invalidar" é como uma delas fica
 * para trás.
 */
export function useInvalidateIncidents() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    /*
     * `pathKey()`, e NÃO `queryKey()`: para uma consulta paginada (`useInfiniteQuery`) o
     * tRPC guarda a chave como `[['incident','list'], { input, type: 'infinite' }]`, e
     * `queryKey()` sem input monta `[..., { type: 'query' }]` — que não casa com nada.
     * O sintoma é traiçoeiro: a mutação responde 200, o contador do menu (consulta comum)
     * cai na hora e a LISTA continua mostrando o estado velho. Medido com o tRPC 11.18.
     */
    void queryClient.invalidateQueries({ queryKey: trpc.incident.list.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.incident.counts.queryKey() });
  };
}

/** "Eu vi e estou cuidando". NÃO fecha o incidente — para isso existe `useResolveIncident`. */
export function useAcknowledgeIncident() {
  const trpc = useTRPC();
  const invalidate = useInvalidateIncidents();
  return useMutation(trpc.incident.acknowledge.mutationOptions({ onSuccess: invalidate }));
}

/** "Acabou". Se o mesmo problema voltar, o servidor abre um incidente NOVO. */
export function useResolveIncident() {
  const trpc = useTRPC();
  const invalidate = useInvalidateIncidents();
  return useMutation(trpc.incident.resolve.mutationOptions({ onSuccess: invalidate }));
}

/* ========================================================================== */
/*  Meta Ads (Fase 8)                                                         */
/* ========================================================================== */

/**
 * CONTRATO REAL (de `apps/api/src/modules/ads/ads.router.ts`):
 *
 *   ads.connections { workspaceId }                              -> AdsConnection[]  (integration:read)
 *   ads.connect     { workspaceId, externalAccountId, tokenRef } -> AdsConnection    (integration:manage)
 *   ads.disconnect  { workspaceId, connectionId }                -> { ok: true }     (integration:manage)
 *   ads.entities    { workspaceId, connectionId, level? }        -> AdsEntity[]      (integration:read)
 *   ads.series      { workspaceId, level, since, until, … }      -> { rows, totals } (analytics:read)
 *
 * Quatro coisas que a tela não pode esquecer:
 *
 *  - **Não existe `ads.syncNow`, e a ausência é decisão de projeto.** Sincronizar é trabalho do
 *    worker, que tem a credencial e o agendamento; um botão que disparasse sync por HTTP daria a
 *    uma requisição o poder de queimar a cota da conta do CLIENTE, que é o recurso escasso desta
 *    integração.
 *  - **`tokenRef` é PONTEIRO, nunca token.** O servidor recusa por regex qualquer coisa fora de
 *    `env:NOME_DA_VARIAVEL`. O formulário valida a mesma forma antes de mandar — não como defesa
 *    (quem defende é o servidor), mas para a recusa ser uma frase que ensina, em vez de um 400
 *    seco depois de o token já ter viajado pela rede.
 *  - **A leitura não passa pelo provider.** `series` e `entities` saem do que o job gravou. Token
 *    morto NÃO é erro de carregamento: a conexão vem `TOKEN_EXPIRED` e os números continuam
 *    respondendo — a tela avisa que estão velhos em vez de virar tela de erro.
 *  - **`null` nas razões é "não há o que medir", nunca `0`** (invariante 8): `cpcCents` sem
 *    clique, `cpmCents`/`ctr` sem impressão, `reach` que a Meta não informou.
 */

/* -------------------------------------------------------------------------- */
/*  Tipos                                                                      */
/* -------------------------------------------------------------------------- */

export type AdsConnection = QueryOutput<ApiClient['ads']['connections']>[number];
/** `'ACTIVE' | 'TOKEN_EXPIRED' | 'ERROR' | 'DISABLED'`, vindo do enum do banco. */
export type AdsConnectionStatus = AdsConnection['status'];

export type AdsSeriesPage = QueryOutput<ApiClient['ads']['series']>;
export type AdsMetricRow = AdsSeriesPage['rows'][number];
export type AdsTotals = AdsSeriesPage['totals'];
/** Uma linha por campanha/conjunto/anúncio no período, somada no servidor (09/10). */
export type AdsResumoPage = QueryOutput<ApiClient['ads']['resumo']>;
export type AdsResumoRow = AdsResumoPage['rows'][number];

export type AdsEntity = QueryOutput<ApiClient['ads']['entities']>[number];

/**
 * `'CAMPAIGN' | 'ADSET' | 'AD'` — sai do INPUT do router, não de um literal escrito
 * aqui. Se a Meta ganhar um nível novo e o servidor o aceitar, é o typecheck desta
 * tela que cobra a aba correspondente.
 */
export type AdsLevel = NonNullable<QueryInput<ApiClient['ads']['series']>['level']>;

export type ConnectAdsInput = MutationInput<ApiClient['ads']['connect']>;

/* -------------------------------------------------------------------------- */
/*  Hooks                                                                      */
/* -------------------------------------------------------------------------- */

export function useAdsConnections(workspaceId: string, enabled = true) {
  const trpc = useTRPC();
  return useQuery({ ...trpc.ads.connections.queryOptions({ workspaceId }), ...AO_VIVO, enabled });
}

/**
 * A árvore de campanhas/conjuntos/anúncios de UMA conexão.
 *
 * Serve para a linha da série saber o nome do pai (campanha ▸ conjunto ▸ anúncio).
 * `connectionId` nulo desliga: a lista de conexões chega depois da montagem, e uma
 * consulta disparada com id vazio volta 400 em toda navegação.
 */
export function useAdsEntities(workspaceId: string, connectionId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.ads.entities.queryOptions({ workspaceId, connectionId: connectionId ?? '' }),
    enabled: connectionId !== null && connectionId !== '',
    /** A hierarquia muda quando alguém cria campanha, não a cada minuto. */
    staleTime: 5 * 60_000,
  });
}

export interface AdsSeriesArgs {
  level: AdsLevel;
  /** `YYYY-MM-DD`, no fuso da CONTA DE ANÚNCIO (ver `ads-ui.ts`). */
  since: string;
  until: string;
  connectionId?: string | null;
  /** "As campanhas que eu quero ver". Vazio = todas. */
  campaignExternalIds?: readonly string[];
  /** "Ocultar campanha" — some da tabela e dos totais (por pessoa, guardado no navegador). */
  excludeCampaignExternalIds?: readonly string[];
  limit?: number;
}

/**
 * A série do período.
 *
 * O recorte (`since`/`until`) é montado pela tela, e não por um preset resolvido no
 * servidor como em `analytics.*`: aqui o dia é o da CONTA DE ANÚNCIO (a Insights API
 * fecha o dia no fuso dela e não aceita outro), então quem sabe qual fuso usar é
 * quem tem a conexão na mão. Está anotado no relatório da fase como a diferença que
 * um `period` de `ads.series` resolveria.
 */
export function useAdsSeries(workspaceId: string, args: AdsSeriesArgs, enabled = true) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.ads.series.queryOptions({
      workspaceId,
      level: args.level,
      since: args.since,
      until: args.until,
      ...(args.connectionId ? { connectionId: args.connectionId } : {}),
      ...(args.campaignExternalIds && args.campaignExternalIds.length > 0
        ? { campaignExternalIds: [...args.campaignExternalIds] }
        : {}),
      ...(args.excludeCampaignExternalIds && args.excludeCampaignExternalIds.length > 0
        ? { excludeCampaignExternalIds: [...args.excludeCampaignExternalIds] }
        : {}),
      ...(args.limit ? { limit: args.limit } : {}),
    }),
    enabled,
  });
}

/** O mesmo recorte da série, uma linha por entidade (09/10) — a visão padrão da tela. */
export function useAdsResumo(workspaceId: string, args: AdsSeriesArgs, enabled = true) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.ads.resumo.queryOptions({
      workspaceId,
      level: args.level,
      since: args.since,
      until: args.until,
      ...(args.connectionId ? { connectionId: args.connectionId } : {}),
      ...(args.excludeCampaignExternalIds && args.excludeCampaignExternalIds.length > 0
        ? { excludeCampaignExternalIds: [...args.excludeCampaignExternalIds] }
        : {}),
    }),
    enabled,
  });
}

/**
 * Invalida o que uma escrita em conexão desloca.
 *
 * `pathKey()` em `series` e `entities`, e não `queryKey()`: as duas são consultadas
 * com input variável (nível, período, conexão), e `queryKey()` sem input casa só com
 * a chave exata daquele procedure sem argumento. Conectar uma conta nova e ver a
 * tabela velha na tela é o sintoma de quem errou isso.
 */
function useInvalidateAds() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.ads.connections.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.ads.entities.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.ads.series.pathKey() });
  };
}

export function useConnectAdsAccount() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAds();
  return useMutation(trpc.ads.connect.mutationOptions({ onSuccess: invalidate }));
}

export type AdsConnectionTest = MutationOutput<ApiClient['ads']['testConnection']>;

/** "Testar conexão" no formulário: pergunta à Meta, não grava nada. */
export function useTestAdsToken() {
  const trpc = useTRPC();
  return useMutation(trpc.ads.testToken.mutationOptions());
}

/** "Sincronizar agora": enfileira uma leitura desta conta (trava de 5 min por conta). */
export function useSyncAdsNow() {
  const trpc = useTRPC();
  return useMutation(trpc.ads.syncNow.mutationOptions());
}

/** Reconsultar o estado das conexões — o cartão usa enquanto espera a leitura pedida. */
export function useRefreshAdsConnections() {
  return useInvalidateAds();
}

/** "Testar conexão" no cartão: mede agora e grava o resultado no status da conexão. */
export function useTestAdsConnection() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAds();
  return useMutation(trpc.ads.testConnection.mutationOptions({ onSuccess: invalidate }));
}

/** Trocar o token de uma conexão existente — validado na Meta e guardado cifrado (ADR-0017). */
export function useReplaceAdsToken() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAds();
  return useMutation(trpc.ads.replaceToken.mutationOptions({ onSuccess: invalidate }));
}

/** Desconectar leva junto o espelho (hierarquia e métrica). Não leva os cliques. */
export function useDisconnectAdsAccount() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAds();
  return useMutation(trpc.ads.disconnect.mutationOptions({ onSuccess: invalidate }));
}

/* ========================================================================== */
/*  Lucro por lead — a comissão do dia lançada à mão (Fase 9)                  */
/* ========================================================================== */

/**
 * CONTRATO (conferido em `apps/api/src/modules/earnings/earnings.router.ts`):
 *
 *   earnings.day    { workspaceId, date }                       -> DayEarnings
 *   earnings.range  { workspaceId, since, until }               -> { since, until, days, summary }
 *   earnings.upsert { workspaceId, date, groupId, amountCents } -> DayEarnings
 *   earnings.remove { workspaceId, date, groupId }              -> DayEarnings
 *
 * Os tipos saem do router, como todo o resto deste arquivo — esta tela foi escrita em
 * paralelo ao servidor contra um contrato combinado, e é justamente por isso que derivar
 * importa: é o typecheck, e não a produção, que descobre uma divergência entre os dois lados.
 *
 * **Três coisas que a tela NÃO faz** (e que valem mais do que a lista de tipos):
 *
 *  1. **Não divide nada.** `profitPerLeadMillicents` e `band` chegam prontos (invariante 3);
 *     os LIMITES das faixas nunca aparecem no código da tela — se aparecessem, o dia em que
 *     o servidor mudasse a régua a tela continuaria desenhando, agora mentindo.
 *  2. **Não decide que dia é hoje.** O dia do negócio vem do servidor, resolvido no fuso do
 *     workspace (`analytics.overview.period.to`). Às 22h em São Paulo o relógio do navegador
 *     de quem está em Lisboa já diz amanhã.
 *  3. **Não transforma `null` em `0`.** `totalCents: null` é NÃO LANÇADO; `0` é zero lançado.
 *     `profitPerLeadMillicents: null` é ausência de denominador, não lucro zero. Cada um vira
 *     travessão com a frase do seu motivo.
 *
 * Unidades, porque errar uma delas não quebra nada e vira número errado em silêncio:
 * dinheiro em **centavos inteiros**, lucro por lead em **milésimos de centavo**
 * (R$ 0,03/lead = `3000`).
 */

export type DayEarnings = QueryOutput<ApiClient['earnings']['day']>;
export type DayEarningsGroup = DayEarnings['groups'][number];
/** `'BAIXO' | 'NORMAL' | 'IDEAL' | 'MUITO_BOM'` — quem classifica é o servidor. */
export type EarningsBand = NonNullable<DayEarnings['band']>;
/**
 * O aviso já vem com a frase pronta em pt-BR. A tela EXIBE; ela não monta o texto nem
 * corrige o número por conta própria — valor implausível se avisa, nunca se conserta
 * sozinho, senão o painel passa a discordar do que a pessoa digitou sem dizer.
 */
export type EarningsWarning = DayEarnings['warnings'][number];
/** Por que `membersEod` é o que é: medido, sem grupos ativos, ou cobertura incompleta. */
export type EarningsMembersStatus = DayEarnings['membersEodStatus'];
export type EarningsPeriod = QueryOutput<ApiClient['earnings']['range']>;
export type EarningsUpsertInput = MutationInput<ApiClient['earnings']['upsert']>;
export type EarningsRemoveInput = MutationInput<ApiClient['earnings']['remove']>;

/**
 * COMISSÃO POR MARKETPLACE (18/09) — Shopee, Amazon e Mercado Livre.
 *
 * O contrato acima ganhou `marketplace` em `upsert` e `remove`, e **ele é obrigatório**:
 * quem lança diz de quem é o dinheiro. Não há default nem no input nem na coluna — um
 * default faria todo lançamento que esquecesse de declarar virar Shopee em silêncio, com o
 * total continuando a fechar.
 *
 *   earnings.upsert { …, marketplace, amountCents } -> DayEarnings
 *   earnings.remove { …, marketplace }              -> DayEarnings
 *
 * ⚠️ **O denominador NÃO se divide entre eles.** Cada fatia vem dividida pela MESMA base de
 * pessoas do geral, porque a mesma pessoa no grupo pode comprar na Shopee e na Amazon no
 * mesmo dia. É por isso que o geral é a soma dos parciais — e é o servidor quem faz essa
 * conta, como todas as outras (invariante 3). A tela imprime.
 *
 * `amountCents: null` numa fatia é **NÃO LANÇADO**; `0` é zero lançado. Os três marketplaces
 * vêm sempre na resposta, justamente para o não lançado aparecer em vez de sumir.
 */
export type EarningsMarketplaceSlice = DayEarnings['marketplaces'][number];
/** `'SHOPEE' | 'AMAZON' | 'MERCADO_LIVRE'` — derivado do router, nunca digitado à mão. */
export type EarningsMarketplaceName = EarningsMarketplaceSlice['marketplace'];
/** A fatia do PERÍODO, com `daysWithEntry` junto (invariante 9). */
export type EarningsPeriodMarketplace = EarningsPeriod['summary']['byMarketplace'][number];

/**
 * A permissão de ESCRITA do lançamento.
 *
 * `earnings:write` vale de ANALYST para cima: é dado de operação, digitado todo fim de
 * expediente por quem toca o dia a dia, e não o faturamento deste produto. Ler o painel
 * continua sendo `analytics:read`, que todo mundo tem — a assimetria é de propósito, porque
 * um número errado aqui não quebra nada e vira decisão de compra de tráfego em silêncio.
 *
 * Fica numa constante, e não espalhada pelas telas, porque é o ponto que precisa combinar com
 * o servidor. Esconder por `can()` é UX, não segurança: quem barra de verdade é ele.
 */
export const EARNINGS_WRITE_PERMISSION = 'earnings:write' as const;

/**
 * O lançamento de UM dia.
 *
 * `date` nulo desliga a consulta: quem sabe que dia é "hoje" é o servidor (ele resolve no
 * fuso do workspace, em `analytics.overview.period.to`), e o painel só descobre isso depois
 * da primeira resposta. Disparar com data vazia renderia 400 em toda abertura do dashboard.
 */
export function useEarningsDay(workspaceId: string, date: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.earnings.day.queryOptions({ workspaceId, date: date ?? '' }),
    enabled: date !== null && date !== '',
  });
}

/** Os últimos dias, para enxergar o que ficou sem lançar. `since`/`until` são dias do negócio. */
export function useEarningsRange(
  workspaceId: string,
  since: string | null,
  until: string | null,
  enabled = true,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.earnings.range.queryOptions({
      workspaceId,
      since: since ?? '',
      until: until ?? '',
    }),
    enabled: enabled && since !== null && until !== null && since !== '' && until !== '',
  });
}

/**
 * Invalida o que um lançamento desloca.
 *
 * `pathKey()`, nunca `queryKey()`: as duas consultas levam input variável (a data, a janela),
 * e `queryKey()` sem input casa só com a chave daquele procedure SEM argumento — que não é
 * nenhuma das que estão na tela. É a terceira vez que esta armadilha aparece no repo.
 */
function useInvalidateEarnings() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.earnings.day.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.earnings.range.pathKey() });
  };
}

export function useUpsertEarnings() {
  const trpc = useTRPC();
  const invalidate = useInvalidateEarnings();
  return useMutation(trpc.earnings.upsert.mutationOptions({ onSuccess: invalidate }));
}

/**
 * Apagar o lançamento é diferente de lançar zero: volta o dia para "não lançado".
 * Sem esta rota, um dia lançado por engano só teria como virar `R$ 0,00` — e zero é uma
 * afirmação sobre o negócio ("não vendi nada"), não um jeito de desfazer.
 */
export function useRemoveEarnings() {
  const trpc = useTRPC();
  const invalidate = useInvalidateEarnings();
  return useMutation(trpc.earnings.remove.mutationOptions({ onSuccess: invalidate }));
}

/* ========================================================================== */
/*  Assinatura / planos (Fase 10)                                              */
/* ========================================================================== */

/**
 * CONTRATO REAL (`apps/api/src/modules/billing/billing.router.ts`):
 *
 *   billing.catalog            (PÚBLICO, sem sessão)   -> { plans, featureKeys }
 *   billing.current            { workspaceId }         -> plano + assinatura + usage
 *   billing.startTrial         { workspaceId, planCode }
 *   billing.requestPlanChange  { workspaceId, planCode, interval }
 *                                                      -> { ok, contact }
 *
 * **Nada aqui cobra.** Não existe gateway nesta entrega: `requestPlanChange`
 * grava a intenção no log de auditoria e devolve `PAGAMENTO_INDISPONIVEL`. A
 * tela diz isso com todas as letras, antes e depois do clique — ver
 * `features/billing/AvisoDePagamento.tsx`. Uma tela que finge cobrar vira
 * "paguei e não liberou" no suporte.
 *
 * Duas coisas que a tela NÃO pode esquecer:
 *  - `monthlyEquivalentCents` e `discountPercent` chegam PRONTOS (invariante 3).
 *    Dividir o total pelos meses aqui criaria um segundo caminho para o mesmo
 *    número, e um dos dois ficaria errado depois do primeiro reajuste.
 *  - `-1` viaja como `-1` e quer dizer "ilimitado"; `0` quer dizer "não
 *    incluso". Traduzir os dois para vazio apagaria a diferença entre um limite
 *    e a ausência de dado (invariante 8).
 *
 * Este bloco nasceu escrito à mão, contra `docs/features/billing-contrato.md`,
 * enquanto a api era escrita em paralelo. Assim que `billing` entrou no router,
 * os tipos passaram a sair DELE, como todo o resto do arquivo — e a conferência
 * pegou, antes do primeiro clique, que `startTrial` e `requestPlanChange`
 * exigem `workspaceId`, que o contrato escrito não mencionava.
 */

export type BillingCatalog = QueryOutput<ApiClient['billing']['catalog']>;
export type CatalogPlan = BillingCatalog['plans'][number];
export type PlanPrice = CatalogPlan['prices'][number];

export type BillingCurrent = QueryOutput<ApiClient['billing']['current']>;
/** Uso de AGORA contra o limite, chave a chave. É o que pinta "3 de 3 grupos". */
export type UsageRow = BillingCurrent['usage'][number];
export type BillingSubscription = BillingCurrent['subscription'];

export type StartTrialInput = MutationInput<ApiClient['billing']['startTrial']>;
export type RequestPlanChangeInput = MutationInput<ApiClient['billing']['requestPlanChange']>;
export type PlanChangeRequest = MutationOutput<ApiClient['billing']['requestPlanChange']>;

/** `'MONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'YEARLY'` — o ciclo de cobrança. */
export type BillingInterval = RequestPlanChangeInput['interval'];

/**
 * A ordem em que os ciclos aparecem na tela mora em `features/billing`, junto
 * do rótulo em português. Aqui fica só o que o servidor define.
 */
export const PAGAMENTO_INDISPONIVEL = 'PAGAMENTO_INDISPONIVEL';

/**
 * O catálogo público: é o que a vitrine `/planos` mostra para quem ainda não
 * tem conta. `staleTime` alto de propósito — preço de tabela não muda entre
 * dois cliques, e esta é a tela que mais gente abre sem estar logada.
 */
export function useBillingCatalog() {
  const trpc = useTRPC();
  return useQuery({ ...trpc.billing.catalog.queryOptions(), staleTime: 10 * 60_000 });
}

/** O plano vigente do workspace + o consumo real contra cada limite. */
export function useBillingCurrent(workspaceId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.billing.current.queryOptions({ workspaceId: workspaceId ?? '' }),
    enabled: workspaceId !== null && workspaceId !== '',
  });
}

/**
 * Trial: é a ÚNICA rota desta entrega que muda o que o workspace pode fazer.
 *
 * Por isso invalida TUDO, e não só o plano: o limite avaliado viaja junto da
 * lista de monitoradores, de grupos, de campanhas e de redirecionadores, e uma
 * dessas telas com o número velho diz "sem vaga" para quem acabou de ganhar
 * vaga — o erro aparece longe da causa, três telas adiante.
 */
export function useStartTrial() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.billing.startTrial.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries();
      },
    }),
  );
}

/**
 * Pedido de troca de plano. **Não cobra e não troca nada**: grava a intenção e
 * devolve `PAGAMENTO_INDISPONIVEL`.
 *
 * Não invalida nada de propósito — nada mudou. Um refetch aqui daria à tela o
 * ar de "processando", que é exatamente a mentira que esta entrega não conta.
 */
export function useRequestPlanChange() {
  const trpc = useTRPC();
  return useMutation(trpc.billing.requestPlanChange.mutationOptions());
}

/* ========================================================================== */
/*  Treinamentos (Fase 11)                                                    */
/* ========================================================================== */

/**
 * CONTRATO (Fase 11 — `apps/api/src/modules/training/training.router.ts`):
 *
 *   training.courses      {}                            -> CourseCard[]
 *   training.course       { courseSlug }                 -> CourseDetail
 *   training.lesson       { courseSlug, lessonSlug }     -> LessonDetail
 *   training.complete     { lessonId }                   -> { completedAt, alreadyCompleted }
 *   training.savePosition { lessonId, seconds }          -> { lastPositionSeconds, completedAt }
 *
 * **Nenhuma dessas rotas leva `workspaceId`, e isso não é esquecimento.** O conteúdo é
 * global (a MGDLead publica o curso uma vez para todos os clientes) e o progresso é da
 * PESSOA, resolvido da sessão no servidor. A tela vive dentro do workspace porque é lá que
 * está o menu; o dado, não. Mandar um `workspaceId` daria a essas rotas a aparência de
 * serem por tenant sem nenhuma consulta filtrando por tenant.
 *
 * Três coisas que a tela NÃO pode esquecer:
 *  - **`videoUrl: null` é "o vídeo ainda não subiu"**, não erro e não aula inexistente.
 *    A aula aparece e a tela diz isso em palavras (invariante 12).
 *  - **`completedAt` e `lastPositionSeconds` são coisas diferentes.** Arrastar o player
 *    até o fim é avanço; concluir é um clique de gente. A tela nunca deduz um do outro.
 *  - **`0 de 12` é medido, `—` seria "não sei".** Enquanto a lista não chega é skeleton,
 *    nunca um zero (invariante 8).
 */

export type TrainingCourseCard = QueryOutput<ApiClient['training']['courses']>[number];
export type TrainingCourseDetail = QueryOutput<ApiClient['training']['course']>;
export type TrainingModule = TrainingCourseDetail['modules'][number];
export type TrainingLessonSummary = TrainingModule['lessons'][number];
export type TrainingLessonDetail = QueryOutput<ApiClient['training']['lesson']>;

/** Os cursos publicados, com o progresso desta pessoa em cada um. */
export function useTrainingCourses() {
  const trpc = useTRPC();
  return useQuery(trpc.training.courses.queryOptions());
}

/** Um curso: módulos e aulas em ordem. `slug` vazio (rota ainda montando) desliga. */
export function useTrainingCourse(courseSlug: string) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.training.course.queryOptions({ courseSlug }),
    enabled: courseSlug.length > 0,
  });
}

export function useTrainingLesson(courseSlug: string, lessonSlug: string) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.training.lesson.queryOptions({ courseSlug, lessonSlug }),
    enabled: courseSlug.length > 0 && lessonSlug.length > 0,
  });
}

/**
 * Invalida o que uma conclusão desloca: a aula (o botão vira "concluída"), o curso (o
 * "3 de 12" anda) e a lista (o cartão do curso mostra o mesmo número). Invalidar só a
 * aula deixaria a lista jurando "2 de 12" com a tela do curso mostrando 3 — e a lista é
 * o que a pessoa olha primeiro quando volta.
 */
function useInvalidateTraining() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.training.lesson.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.training.course.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.training.courses.pathKey() });
  };
}

/** "Marcar como assistida". Idempotente no servidor — clicar duas vezes não conta duas. */
export function useCompleteLesson() {
  const trpc = useTRPC();
  const invalidate = useInvalidateTraining();
  return useMutation(trpc.training.complete.mutationOptions({ onSuccess: invalidate }));
}

/**
 * Onde a pessoa parou no vídeo. **Não conclui nada** — e por isso NÃO invalida o
 * progresso: um refetch aqui faria a tela piscar a cada poucos segundos de reprodução
 * para atualizar um número que não mudou.
 */
export function useSaveLessonPosition() {
  const trpc = useTRPC();
  return useMutation(trpc.training.savePosition.mutationOptions());
}

/* ========================================================================== */
/*  Shopee Affiliate (F1/F2) — conexão, importação e lucro por bloco           */
/* ========================================================================== */

/**
 * CONTRATO (conferido em `apps/api/src/modules/shopee/shopee.router.ts`):
 *   shopee.status        { workspaceId }                      -> ShopeeStatus
 *   shopee.connect       { workspaceId, appId, appSecret }    -> ShopeeStatus   (OWNER)
 *   shopee.disconnect    { workspaceId }                      -> ShopeeStatus   (OWNER)
 *   shopee.syncNow       { workspaceId }                      -> { queued, cooldownSeconds }
 *   shopee.subIds        { workspaceId, since, until }        -> ShopeeSubIdRow[]
 *   shopee.linkSubId     { workspaceId, subId, campaignId }   -> { subId, campaignId }
 *   shopee.unlinkSubId   { workspaceId, subId }               -> { ok }
 *   shopee.profitByBlock { workspaceId, since, until }        -> ShopeeBlockProfit
 *
 * A senha da API só VAI (no `connect`); nunca volta — o status traz o fim dela (`…a1b2`).
 * O período da tela sai da janela MEDIDA que o servidor devolve, nunca do relógio do
 * navegador (invariante 10).
 */
export type ShopeeStatus = QueryOutput<ApiClient['shopee']['status']>;
export type ShopeeConnectionView = NonNullable<ShopeeStatus['connection']>;
export type ShopeeSubIdRow = QueryOutput<ApiClient['shopee']['subIds']>[number];
export type ShopeeBlockProfit = QueryOutput<ApiClient['shopee']['profitByBlock']>;
export type ShopeeBlockProfitRow = ShopeeBlockProfit['blocks'][number];
export type ShopeeBlockGap = NonNullable<ShopeeBlockProfitRow['gap']>;

/** Conectar/remover a credencial é só do OWNER (F1.4). Esconder é UX; quem barra é o servidor. */
export const SHOPEE_CONNECT_PERMISSION = 'earnings:connect' as const;

export function useShopeeStatus(workspaceId: string) {
  const trpc = useTRPC();
  return useQuery(trpc.shopee.status.queryOptions({ workspaceId }));
}

export function useShopeeSubIds(
  workspaceId: string,
  period: { since: string; until: string } | null,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.shopee.subIds.queryOptions({
      workspaceId,
      since: period?.since ?? '',
      until: period?.until ?? '',
    }),
    enabled: period !== null,
  });
}

export function useShopeeProfitByBlock(
  workspaceId: string,
  period: { since: string; until: string } | null,
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.shopee.profitByBlock.queryOptions({
      workspaceId,
      since: period?.since ?? '',
      until: period?.until ?? '',
    }),
    enabled: period !== null,
  });
}

/** Tudo que a conexão desloca: o status, os Sub IDs, o lucro por bloco e o lucro por lead. */
function useInvalidateShopee() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.shopee.status.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.shopee.subIds.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.shopee.profitByBlock.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.earnings.day.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.earnings.range.pathKey() });
  };
}

export function useShopeeConnect() {
  const trpc = useTRPC();
  const invalidate = useInvalidateShopee();
  return useMutation(trpc.shopee.connect.mutationOptions({ onSuccess: invalidate }));
}

export function useShopeeDisconnect() {
  const trpc = useTRPC();
  const invalidate = useInvalidateShopee();
  return useMutation(trpc.shopee.disconnect.mutationOptions({ onSuccess: invalidate }));
}

export function useShopeeSyncNow() {
  const trpc = useTRPC();
  return useMutation(trpc.shopee.syncNow.mutationOptions());
}

export function useLinkShopeeSubId() {
  const trpc = useTRPC();
  const invalidate = useInvalidateShopee();
  return useMutation(trpc.shopee.linkSubId.mutationOptions({ onSuccess: invalidate }));
}

export function useUnlinkShopeeSubId() {
  const trpc = useTRPC();
  const invalidate = useInvalidateShopee();
  return useMutation(trpc.shopee.unlinkSubId.mutationOptions({ onSuccess: invalidate }));
}
